<script>
  import { API_BASE_URL, EMAIL_CONFIG, isSuperAdmin as checkSuperAdmin, isAdminUser } from './config.js';
  import { onMount, onDestroy } from 'svelte';
  import AgGridSvelte from 'ag-grid-svelte';
  import 'ag-grid-community/styles/ag-grid.css';
  import 'ag-grid-community/styles/ag-theme-alpine.css';
  import Navigation from './components/layout/Navigation.svelte';
  import LoadingSpinner from './components/common/LoadingSpinner.svelte';
  import ErrorMessage from './components/common/ErrorMessage.svelte';
  import EmptyState from './components/common/EmptyState.svelte';
  import StatusBadge from './components/common/StatusBadge.svelte';
  import BaseModal from './components/modals/BaseModal.svelte';
  import FormModal from './components/modals/FormModal.svelte';
  import ConfirmModal from './components/modals/ConfirmModal.svelte';
  import Button from './components/common/Button.svelte';
  
  export let setPage;
  export let user;
  export let initialApplicantId = null; // URL에서 전달받은 초기 지원자 ID
  export let initialShowDetail = false; // URL에서 전달받은 상세 모달 표시 여부

  // 공통 API 응답 처리 함수
  function handleApiResponse(response) {
    if (response.status === 401 || response.status === 403) {
      console.warn('인증 실패. 로그인 페이지로 이동합니다.');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      alert('세션이 만료되었습니다. 다시 로그인해주세요.');
      window.location.href = '/?t=' + Date.now() + '#login';
      return false;
    }
    return true;
  }
  
  let isLoading = false;
  let loadError = null;
  let projects = []; // 모든 프로젝트 목록
  let selectedProject = null; // 선택된 프로젝트
  let applicants = []; // 지원자 목록
  let isMenuOpen = false; // 메뉴 상태 추가
  let isDropdownOpen = false; // 드롭다운 상태 추가
  let isAdmin = false; // 관리자 권한 여부
  let isSuperAdmin = false; // 슈퍼 관리자 여부
  let isCompany = false; // 기업 사용자 여부
  let selectedStatusFilter = 'all'; // 지원공고 목록 상태 필터
  
  // === 반응형(모바일) 지원을 위한 변수 및 함수 추가 ===
  let isMobile = false;

  function checkMobileDevice() {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent.toLowerCase();
    const keywords = ['android', 'iphone', 'ipad', 'ipod', 'windows phone', 'mobile'];
    return keywords.some(k => ua.includes(k)) || window.innerWidth < 768;
  }

  function handleResize() {
    const prev = isMobile;
    isMobile = checkMobileDevice();
    if ((gridApi && prev !== isMobile) || (gridProjectApi && prev !== isMobile)) {
      // 레이아웃 변화 시 그리드 높이 재계산
      if (gridApi) {
        gridApi.setDomLayout(isMobile ? 'autoHeight' : 'normal');
        gridApi.resetRowHeights();
        gridApi.setRowData(applicants);
      }
      if (gridProjectApi) {
        gridProjectApi.setDomLayout(isMobile ? 'autoHeight' : 'normal');
        gridProjectApi.resetRowHeights();
        gridProjectApi.setRowData(applicantProjects);
      }
    }
  }

  // ======= 모바일 카드 렌더러 (지원자 목록용) =======
  function applicantCardRenderer(params) {
    const d = params.data;
    if (!d) return '';
    
    const statusObj = statusOptions.find(s => s.id === d.status) || statusOptions[0];
    const statusLabel = statusObj.label;
    const priceFormatted = formatPrice(d.price);
    const appliedDate = d.applied_at || d.appliedAt || d.createdAt;
    const appliedDateFormatted = appliedDate ? 
      new Date(appliedDate).toLocaleDateString('ko-KR', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\./g, '.').replace(/\s/g, '') : '-';
    
    const blacklistTag = d.isBlacklist ? '<div class="applicant-blacklist-tag">Blacklist</div>' : '';
    
    // 첨부 문서 버튼 배경색 결정
    const attachBtnBg = d.hasFiles === true ? '#4A90E2' : '#6c757d';
    const attachBtnText = '📁 문서';
    
    // 상태 선택 옵션들 생성
    const statusOptions_html = statusOptions.map(option => 
      `<option value="${option.id}" ${option.id === d.status ? 'selected' : ''}>${option.label}</option>`
    ).join('');

    return `
      <div class="mobile-card">
        <div class="card-header">
          <div class="checkbox-container">
            <input type="checkbox" class="mobile-checkbox" data-applicant-id="${d.id}" />
          </div>
          <div class="name">
            <span class="applicant-name-text">${d.name || ''}</span>
            ${blacklistTag}
          </div>
        </div>
        <div class="card-body">
          <div class="card-row"><strong>나이:</strong> ${d.age || '-'}</div>
          <div class="card-row"><strong>단가(만원):</strong> ${priceFormatted}</div>
          <div class="card-row"><strong>지원일자:</strong> ${appliedDateFormatted}</div>
          <div class="card-row status-row">
            <strong>상태:</strong>
            <select class="mobile-status-select" data-applicant-id="${d.id}">
              ${statusOptions_html}
            </select>
          </div>
          <div class="card-row attachment-row">
            <strong>첨부 문서:</strong>
            <button class="profile-docs-btn js-mobile-docs-btn" style="background:${attachBtnBg}; color:#fff;" data-applicant-id="${d.id}">
              ${attachBtnText}
            </button>
          </div>
          <div class="card-row actions-row">
            <button class="detail-btn js-mobile-detail-btn" data-applicant-id="${d.id}"><span class="detail-icon">📋</span> 상세정보</button>
          </div>
        </div>
      </div>
    `;
  }

  // ======= 모바일 카드 렌더러 (지원자의 프로젝트 목록용) =======
  function applicantProjectCardRenderer(params) {
    const d = params.data;
    if (!d) return '';
    
    const statusObj = statusOptions.find(s => s.id === d.status) || statusOptions[0];
    const statusLabel = statusObj.label;
    const priceFormatted = formatPrice(d.price);
    const appliedDate = d.applied_at || d.appliedAt || d.createdAt;
    const appliedDateFormatted = appliedDate ? 
      new Date(appliedDate).toLocaleDateString('ko-KR', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\./g, '.').replace(/\s/g, '') : '-';
    
    // 첨부 문서 버튼 배경색 결정
    const attachBtnBg = d.hasFiles === true ? '#4A90E2' : '#6c757d';
    const attachBtnText = '📁 문서';
    
    // 상태 선택 옵션들 생성
    const statusOptions_html = statusOptions.map(option => 
      `<option value="${option.id}" ${option.id === d.status ? 'selected' : ''}>${option.label}</option>`
    ).join('');

    return `
      <div class="mobile-card">
        <div class="card-header">
          <div class="name">
            <span class="project-title-text">${d.projectTitle || '프로젝트명 없음'}</span>
          </div>
        </div>
        <div class="card-body">
          <div class="card-row"><strong>단가(만원):</strong> ${priceFormatted}</div>
          <div class="card-row"><strong>지원일자:</strong> ${appliedDateFormatted}</div>
          <div class="card-row status-row">
            <strong>상태:</strong>
            <select class="mobile-status-select" data-project-application-id="${d.id}">
              ${statusOptions_html}
            </select>
          </div>
          <div class="card-row attachment-row">
            <strong>첨부 문서:</strong>
            <button class="profile-docs-btn js-mobile-project-docs-btn" style="background:${attachBtnBg}; color:#fff;" data-project-application-id="${d.id}">
              ${attachBtnText}
            </button>
          </div>
          <div class="card-row actions-row">
            <button class="detail-btn js-mobile-project-detail-btn" data-project-application-id="${d.id}"><span class="detail-icon">📋</span> 상세정보</button>
            <div class="read-status-mobile">
              <input type="checkbox" ${d.is_read === 'Y' ? 'checked' : ''} disabled class="read-status-checkbox-mobile" title="${d.is_read === 'Y' ? '열람됨' : '미열람'}">
              <span class="read-status-label">${d.is_read === 'Y' ? '열람' : '미열람'}</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }
  
  // AG Grid 설정 추가
  let gridApi;
  let gridProjectApi; // 프로젝트 그리드용 API 추가
  let quickFilterText = '';
  
  // ======= 모바일 전용 columnDefs (지원자 목록) =======
  const mobileApplicantColumnDefs = [
    {
      headerName: '지원자 정보',
      field: 'id',
      minWidth: 300,
      flex: 1,
      cellRenderer: 'applicantCardRenderer',
      autoHeight: true,
      wrapText: true,
      sortable: false,
      editable: false,
      checkboxSelection: false // 모바일에서는 카드 내부 체크박스 사용
    }
  ];

  // ======= 모바일 전용 columnDefs (지원자의 프로젝트 목록) =======
  const mobileApplicantProjectColumnDefs = [
    {
      headerName: '프로젝트 정보',
      field: 'id',
      minWidth: 300,
      flex: 1,
      cellRenderer: 'applicantProjectCardRenderer',
      autoHeight: true,
      wrapText: true,
      sortable: false,
      editable: false,
      checkboxSelection: false
    }
  ];

  // AG Grid 컬럼 정의 (지원자 목록용 - 데스크톱)
  const applicantColumnDefs = [
    {
      headerName: '',
      width: 50,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: false,
      pinned: 'left',
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      lockPosition: true,
      cellClass: 'checkbox-cell'
    },
    {
      headerName: '이름',
      field: 'name',
      sortable: true,
      filter: true,
      width: 120,
      cellRenderer: params => {
        const blacklistTag = params.data.isBlacklist ? '<span class="blacklist-tag">Blacklist</span>' : '';
        return `<div class="applicant-name">
          <span class="applicant-name-text">${params.value || ''}</span>
          ${blacklistTag}
        </div>`;
      }
    },
    {
      headerName: '나이',
      field: 'age',
      sortable: true,
      filter: true,
      width: 80,
      valueFormatter: params => params.value || '-'
    },
    {
      headerName: '단가(만원)',
      field: 'price',
      sortable: true,
      filter: true,
      width: 120,
      valueFormatter: params => formatPrice(params.value)
    },
    {
      headerName: '지원일자',
      field: 'applied_at',
      sortable: true,
      filter: true,
      width: 120,
      valueFormatter: params => {
        const date = params.value || params.data.appliedAt || params.data.createdAt;
        if (!date) return '-';
        return new Date(date).toLocaleDateString('ko-KR', {
          year: '2-digit',
          month: '2-digit',
          day: '2-digit'
        }).replace(/\./g, '.').replace(/\s/g, '');
      }
    },
    {
      headerName: '상태',
      field: 'status',
      sortable: true,
      filter: true,
      width: 120,
      cellRenderer: params => {
        const select = document.createElement('select');
        select.className = 'status-select';
        
        statusOptions.forEach(option => {
          const optionElement = document.createElement('option');
          optionElement.value = option.id;
          optionElement.textContent = option.label;
          optionElement.selected = option.id === params.value;
          select.appendChild(optionElement);
        });
        
        select.addEventListener('change', (event) => {
          const applicant = applicants.find(a => a.id === params.data.id);
          if (applicant) {
            updateApplicantStatus(applicant, event.target.value);
          }
        });
        
        return select;
      }
    },
    {
      headerName: '첨부 문서',
      field: 'hasFiles',
      width: 140,
      sortable: false,
      filter: false,
      cellRenderer: params => {
        const hasFiles = params.value === true;
        const bg = hasFiles ? '#4A90E2' : '#6c757d';
        const title = hasFiles ? '첨부 문서 있음 - 클릭하여 확인' : '첨부 문서 없음';
        
        const button = document.createElement('button');
        button.className = 'profile-docs-btn small';
        button.style.background = bg;
        button.style.color = '#fff';
        button.title = title;
        button.innerHTML = '<span class="doc-icon">📁</span> 문서';
        
        button.addEventListener('click', () => {
          const applicant = applicants.find(a => a.id === params.data.id);
          if (applicant) {
            fetchApplicantFiles(applicant);
          }
        });
        
        return button;
      }
    },
    {
      headerName: '관리',
      field: 'actions',
      width: 120,
      sortable: false,
      filter: false,
      cellRenderer: params => {
        const button = document.createElement('button');
        button.className = 'detail-btn small';
        button.innerHTML = '<span class=\"detail-icon\">📋</span> 상세정보';
        
        button.addEventListener('click', () => {
          const applicant = applicants.find(a => a.id === params.data.id);
          if (applicant) {
            viewApplicantDetail(applicant);
          }
        });
        
        return button;
      }
    }
  ];

  // AG Grid 컬럼 정의 (지원자 프로젝트 목록용 - 데스크톱)
  const applicantProjectColumnDefs = [
    {
      headerName: '',
      width: 50,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: false,
      pinned: 'left',
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      lockPosition: true,
      cellClass: 'checkbox-cell'
    },
    {
      headerName: '공고명',
      field: 'projectTitle',
      sortable: true,
      filter: true,
      flex: 2,
      cellRenderer: params => {
        const title = params.value || '프로젝트명 없음';
        const projectId = params.data.projectId;
        const btn = document.createElement('button');
        btn.className = 'project-title-link';
        btn.innerText = title;
        btn.style.background = 'none';
        btn.style.border = 'none';
        btn.style.color = '#3273dc';
        btn.style.cursor = 'pointer';
        btn.style.textDecoration = 'underline';
        btn.onclick = () => {
          if (projectId) {
            openProjectDetailModal(projectId);
          }
        };
        return btn;
      }
    },
    {
      headerName: '단가(만원)',
      field: 'price',
      sortable: true,
      filter: true,
      width: 120,
      valueFormatter: params => formatPrice(params.value)
    },
    {
      headerName: '지원일자',
      field: 'applied_at',
      sortable: true,
      filter: true,
      width: 120,
      valueFormatter: params => {
        const date = params.value || params.data.appliedAt || params.data.createdAt;
        if (!date) return '-';
        return new Date(date).toLocaleDateString('ko-KR', {
          year: '2-digit',
          month: '2-digit',
          day: '2-digit'
        }).replace(/\./g, '.').replace(/\s/g, '');
      }
    },
    {
      headerName: '상태',
      field: 'status',
      sortable: true,
      filter: true,
      width: 120,
      cellRenderer: params => {
        const select = document.createElement('select');
        select.className = 'status-select';
        
        statusOptions.forEach(option => {
          const optionElement = document.createElement('option');
          optionElement.value = option.id;
          optionElement.textContent = option.label;
          optionElement.selected = option.id === params.value;
          select.appendChild(optionElement);
        });
        
        select.addEventListener('change', (event) => {
          const applicant = applicantProjects.find(a => a.id === params.data.id);
          if (applicant) {
            updateApplicantStatus(applicant, event.target.value);
          }
        });
        
        return select;
      }
    },
    {
      headerName: '첨부 문서',
      field: 'hasFiles',
      width: 140,
      sortable: false,
      filter: false,
      cellRenderer: params => {
        const hasFiles = params.value === true;
        const bg = hasFiles ? '#4A90E2' : '#6c757d';
        const title = hasFiles ? '첨부 문서 있음 - 클릭하여 확인' : '첨부 문서 없음';
        
        const button = document.createElement('button');
        button.className = 'profile-docs-btn small';
        button.style.background = bg;
        button.style.color = '#fff';
        button.title = title;
        button.innerHTML = '<span class="doc-icon">📁</span> 문서';
        
        button.addEventListener('click', () => {
          const applicant = applicantProjects.find(a => a.id === params.data.id);
          if (applicant) {
            fetchApplicantFiles(applicant);
          }
        });
        
        return button;
      }
    },
    {
      headerName: '상세정보',
      field: 'actions',
      width: 100,
      sortable: false,
      filter: false,
      cellRenderer: params => {
        const button = document.createElement('button');
        button.className = 'detail-btn small';
        button.innerHTML = '<span class=\"detail-icon\">📋</span> 상세정보';
        
        button.addEventListener('click', () => {
          const applicant = applicantProjects.find(a => a.id === params.data.id);
          if (applicant) {
            // 1. Show the detail modal immediately.
            viewApplicantDetail(applicant);
            
            // 2. Mark as read in the background after a short delay.
            setTimeout(() => {
              markAsRead(applicant);
            }, 100); // A small delay like 100ms should be enough.
          }
        });
        
        return button;
      }
    },
    {
      headerName: '열람상태',
      field: 'is_read',
      width: 80,
      sortable: true,
      filter: true,
      cellRenderer: params => {
        const isRead = params.value === 'Y';
        const container = document.createElement('div');
        container.className = 'read-status-container';
        container.style.display = 'flex';
        container.style.alignItems = 'center';
        container.style.justifyContent = 'center';
        container.style.height = '100%';
        
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.checked = isRead;
        checkbox.disabled = true; // 읽기 전용
        checkbox.className = 'read-status-checkbox';
        checkbox.style.cursor = 'default';
        checkbox.style.transform = 'scale(1.2)';
        checkbox.title = isRead ? '열람됨' : '미열람';
        
        // 체크박스 색상 스타일링
        if (isRead) {
          checkbox.style.accentColor = '#28a745';
        } else {
          checkbox.style.accentColor = '#6c757d';
        }
        
        container.appendChild(checkbox);
        return container;
      }
    }
  ];
  
  // 검색 관련 변수 추가
  let projectSearchTerm = "";
  let applicantSearchTerm = "";
  let filteredProjects = [];
  let filteredAllApplicants = [];
  let allRawApplicants = []; // 모든 지원자 원본 데이터 (반응형)
  
  // 상세 보기 모달 관련 변수 추가
  let isDetailModalOpen = false;
  let selectedApplicant = null;
  
  // 이메일 전송 관련 변수 추가
  let isEmailModalOpen = false;
  let emailSubject = '';
  let emailContent = '';
  let isSendingEmail = false;
  let emailSendError = null;
  let emailSendSuccess = false;
  
  // 인터뷰 등록 관련 변수
  let isInterviewModalOpen = false;
  let interviewForm = {
    projectName: '',
    candidateName: '',
    interviewDate: '',
    location: '',
    contactChannel: '',
    notes: '',
    status: 'scheduled'
  };
  let isSubmittingInterview = false;

  // 인터뷰 등록 모달 열기 (지원자 정보 자동 채움)
  function openInterviewModal() {
    if (!selectedApplicant) return;
    interviewForm = {
      applicationId: selectedApplicant.id,
      projectName: selectedApplicant.projectTitle || '',
      candidateName: selectedApplicant.name || '',
      interviewDate: '',
      location: '',
      contactChannel: '',
      notes: '',
      status: 'scheduled'
    };
    isInterviewModalOpen = true;
  }

  // 인터뷰 등록 API 호출
  async function submitInterview() {
    if (!interviewForm.projectName || !interviewForm.candidateName) {
      alert('프로젝트명과 대상자는 필수입니다.');
      return;
    }
    isSubmittingInterview = true;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/interviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(interviewForm)
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || '인터뷰 등록 실패');
      }
      alert('인터뷰가 등록되었습니다.');
      isInterviewModalOpen = false;
    } catch (error) {
      alert(error.message);
    } finally {
      isSubmittingInterview = false;
    }
  }

  // 인터뷰 상태 목록
  const interviewStatusList = [
    { value: 'applied', label: '지원' },
    { value: 'scheduled', label: '예정' },
    { value: 'completed', label: '완료' },
    { value: 'rejected', label: '불합격' },
    { value: 'accepted', label: '합격' }
  ];

  // 첨부 문서 관련 변수 추가
  let isProfileFilesModalOpen = false;
  let selectedApplicantFiles = [];
  let isLoadingFiles = false;
  let fileLoadError = null;
  
  // 모달이 열릴 때 배경 스크롤 방지
  $: {
    if (isProfileFilesModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }
  
  // Gmail 계정 정보는 config.js에서 가져옴
  const emailConfig = EMAIL_CONFIG;
  
  // 탭 관련 변수 추가
  let activeTab = 'projects'; // 'projects' 또는 'applicants'
  let allApplicants = []; // 전체 지원자 목록 (탭에서 사용)
  let applicantProjects = []; // 선택된 지원자의 프로젝트 목록
  let filteredApplicantProjects = []; // 필터링된 지원자 프로젝트 목록
  let selectedApplicantForList = null; // 목록에서 선택된 지원자
  
  // 제외된 회원 보기 옵션
  let showExcludedUsers = false;
  
  // 마감된 프로젝트 제외 여부 (기본값: true)
  let excludeExpiredProjects = true;
  
  // 컴포넌트 초기화 시 로컬 스토리지에서 사용자 정보 가져오기
  if (typeof window !== 'undefined') {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        user = JSON.parse(storedUser);
        
        // 사용자 역할 확인
        isAdmin = isAdminUser(user);
        // 슈퍼 관리자 확인
        isSuperAdmin = checkSuperAdmin(user.email);
        // 기업 사용자 확인
        isCompany = user.role === 'company';
        
        console.log("사용자 정보 로드:", user.email, "관리자:", isAdmin, "슈퍼관리자:", isSuperAdmin, "기업:", isCompany);
      } catch (error) {
        console.error('사용자 정보 파싱 오류:', error);
        localStorage.removeItem('user');
      }
    }
  }
  
  // 상태 옵션 - API에서 동적으로 로드됨
  let statusOptions = [
    { id: 0, label: '신청됨' }, // 기본값 (API 로드 전 fallback)
  ];

  // 상태 값 매핑 (문자열 -> 숫자) - API 로드 후 동적 생성
  let statusMapping = {};

  // 숫자 -> 문자열 매핑 (기존 API와 호환성 유지를 위해) - API 로드 후 동적 생성
  let reverseStatusMapping = {};

  // 상태 코드 로드 함수 (CD008 그룹에서 조회)
  async function loadStatusCodes() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/codes/details/active/CD008`);
      if (!response.ok) {
        console.warn('상태 코드 로드 실패, 기본값 사용');
        return;
      }

      const result = await response.json();
      if (result.success && Array.isArray(result.data) && result.data.length > 0) {
        // API 응답을 statusOptions 형식으로 변환
        statusOptions = result.data.map(code => ({
          id: parseInt(code.code_value),
          label: code.code_name
        }));

        // statusMapping 동적 생성 (code_value를 키로 사용)
        statusMapping = {};
        reverseStatusMapping = {};
        result.data.forEach(code => {
          const numValue = parseInt(code.code_value);
          // code_name을 snake_case로 변환하여 키로 사용
          const stringKey = code.code_name
            .replace(/[()]/g, '')
            .replace(/\s+/g, '_')
            .toLowerCase();
          statusMapping[stringKey] = numValue;
          reverseStatusMapping[numValue] = stringKey;
        });

        console.log('상태 코드 로드 완료:', statusOptions.length, '개');
      }
    } catch (error) {
      console.error('상태 코드 로드 오류:', error);
    }
  }

  // ======= 반응형 columnDefs, grid 옵션 =======
  $: applicantGridColumnDefs = isMobile ? mobileApplicantColumnDefs : applicantColumnDefs;
  $: applicantProjectGridColumnDefs = isMobile ? mobileApplicantProjectColumnDefs : applicantProjectColumnDefs;

  $: paginationPageSize = isMobile ? 5 : 20;

  $: applicantGridOptions = {
    domLayout: isMobile ? 'autoHeight' : 'normal',
    rowSelection: 'multiple',
    components: { applicantCardRenderer },
    defaultColDef: { resizable: true },
    suppressRowClickSelection: true,
    suppressRowDeselection: false,
    getRowId: (params) => params.data.id,
    deltaSort: true,
    suppressClearOnFillReduction: true,
    rowHeight: 50
  };

  $: applicantProjectGridOptions = {
    domLayout: isMobile ? 'autoHeight' : 'normal',
    rowSelection: 'multiple',
    components: { applicantProjectCardRenderer },
    defaultColDef: { resizable: true },
    suppressRowClickSelection: true,
    suppressRowDeselection: false,
    getRowId: (params) => params.data.id,
    rowHeight: 50
  };
  
  // 생년월일 문자열(birth_date)로부터 나이를 계산하는 헬퍼 함수
  function calculateAge(birthDateStr) {
    if (!birthDateStr) return '';
    
    // 다양한 형식의 생년월일 처리
    let birthDate;
    
    // 문자열을 정규화
    const cleanedDateStr = String(birthDateStr).trim();
    
    // 년도만 있는 경우 (예: "1990", 1990)
    if (/^\d{4}$/.test(cleanedDateStr)) {
      const year = parseInt(cleanedDateStr);
      if (year > 1900 && year <= new Date().getFullYear()) {
        // 년도만 있는 경우 1월 1일로 가정
        birthDate = new Date(year, 0, 1);
      }
    } else {
      // 일반적인 날짜 형식 시도
      birthDate = new Date(cleanedDateStr);
    }
    
    if (!birthDate || isNaN(birthDate)) {
      console.warn('생년월일 파싱 실패:', birthDateStr);
      return '';
    }
    
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--; // 아직 생일이 지나지 않았으면 한 살 빼기
    }
    
    // 나이가 음수이거나 비현실적으로 큰 경우 빈 문자열 반환
    if (age < 0 || age > 150) {
      console.warn('비현실적인 나이 계산됨:', age, '원본 데이터:', birthDateStr);
      return '';
    }
    
    return age;
  }
  
  // 숫자에 콤마를 추가하는 헬퍼 함수
  function formatPrice(price) {
    if (!price) return '-';
    // 숫자만 추출 (만원 단위가 이미 포함된 경우 제거)
    const numericPrice = String(price).replace(/[^\d]/g, '');
    if (!numericPrice) return '-';
    // 콤마 추가
    const formattedPrice = Number(numericPrice).toLocaleString();
    return formattedPrice;
  }
  
  // 사용자 제외 여부(is_excluded) 판단 헬퍼
  function isUserExcluded(user) {
    if (!user) return false;
    // 다양한 형태의 필드명을 모두 고려
    const val = user.is_excluded !== undefined ? user.is_excluded : (user.isExcluded !== undefined ? user.isExcluded : null);
    if (val === undefined || val === null) return false;
    if (typeof val === 'string') {
      const up = val.toUpperCase();
      return up === 'Y' || up === 'TRUE' || up === '1';
    }
    return val === true || val === 1;
  }
  
  // 전화번호 포맷팅 헬퍼 함수
  function formatPhoneNumber(phone) {
    if (!phone || phone === '-') return '-';
    
    // 숫자만 추출
    const numbers = phone.replace(/[^0-9]/g, '');
    
    if (numbers.length === 0) return '-';
    
    // 휴대폰 번호 (010, 011, 016, 017, 018, 019로 시작하는 11자리)
    if (numbers.length === 11 && numbers.startsWith('01')) {
      return `${numbers.substring(0, 3)}-${numbers.substring(3, 7)}-${numbers.substring(7)}`;
    }
    
    // 일반 전화번호 (지역번호 + 7~8자리)
    if (numbers.length >= 9 && numbers.length <= 11) {
      if (numbers.startsWith('02')) {
        // 서울 (02-XXXX-XXXX 또는 02-XXX-XXXX)
        if (numbers.length === 9) {
          return `${numbers.substring(0, 2)}-${numbers.substring(2, 5)}-${numbers.substring(5)}`;
        } else if (numbers.length === 10) {
          return `${numbers.substring(0, 2)}-${numbers.substring(2, 6)}-${numbers.substring(6)}`;
        }
      } else if (numbers.length === 10) {
        // 지역번호 3자리 (031, 032, 033 등)
        return `${numbers.substring(0, 3)}-${numbers.substring(3, 6)}-${numbers.substring(6)}`;
      } else if (numbers.length === 11) {
        // 지역번호 3자리 + 8자리
        return `${numbers.substring(0, 3)}-${numbers.substring(3, 7)}-${numbers.substring(7)}`;
      }
    }
    
    // 기타 경우는 원본 반환 (이미 포맷된 경우 등)
    return phone;
  }
  
  // 경력 정보 포맷팅 헬퍼 함수
  function formatCareer(careerYear, careerMonth) {
    console.log('formatCareer 호출:', { careerYear, careerMonth, 
      yearType: typeof careerYear, monthType: typeof careerMonth });
    
    // 둘 다 null/undefined인 경우에만 데이터 없음으로 처리
    if ((careerYear === null || careerYear === undefined) && 
        (careerMonth === null || careerMonth === undefined)) {
      console.log('둘 다 null/undefined -> 반환: -');
      return '-';
    }
    
    // 숫자로 변환 (NaN 체크 포함)
    const year = isNaN(Number(careerYear)) ? 0 : Number(careerYear);
    const month = isNaN(Number(careerMonth)) ? 0 : Number(careerMonth);
    
    console.log('변환된 값:', { year, month });
    
    // 0년 0개월인 경우에만 신입으로 표시 (명시적으로 0이 입력된 경우)
    if (year === 0 && month === 0) {
      console.log('0년 0개월 -> 반환: 신입');
      return '신입';
    }
    
    // 경력이 있는 경우 'n년 n개월' 형식으로 표시
    let result = '';
    if (year > 0) {
      result += `${year}년`;
    }
    if (month > 0) {
      result += result ? ` ${month}개월` : `${month}개월`;
    }
    
    console.log('최종 결과:', result || '-');
    return result || '-';
  }
  
  // 이전 상태 저장을 위한 변수
  let previousApplicationStatus = {};
  
  // 페이지 로드 시 데이터 가져오기
  onMount(async () => {
    // 토큰 체크
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('토큰이 없습니다. 로그인 페이지로 이동합니다.');
      setPage('login');
      return;
    }

    try {
      // 상태 코드 로드 (CD008)
      await loadStatusCodes();

      // API를 사용하여 프로젝트 가져오기 시도
      await fetchProjects();

      // API를 사용하여 지원자 목록 가져오기 시도
      await fetchAllApplicants();

      // URL에서 전달받은 지원자 ID가 있으면 해당 지원자 자동 선택
      if (initialApplicantId) {
        // 지원자 탭으로 전환
        activeTab = 'applicants';

        // allApplicants에서 해당 userId를 가진 지원자 찾기
        const targetApplicant = allApplicants.find(
          a => String(a.userId) === String(initialApplicantId)
        );

        if (targetApplicant) {
          console.log(`URL에서 지원자 ID ${initialApplicantId} 발견, 자동 선택 실행`);
          // URL 업데이트 없이 선택 (이미 URL에 있으므로)
          await selectApplicant(targetApplicant, false);
          // URL에 /detail이 포함되어 있었으면 상세 모달 자동 열기
          if (initialShowDetail) {
            viewApplicantDetail(targetApplicant);
          }
        } else {
          console.warn(`지원자 ID ${initialApplicantId}를 찾을 수 없습니다.`);
          // 지원자를 찾지 못한 경우 URL을 기본 상태로 복원
          window.history.replaceState(
            { page: 'admin-applicants' },
            '',
            '#admin-applicants'
          );
        }
      }
    } catch (error) {
      console.error("초기 데이터 로드 오류:", error);
      loadError = error.message;
    }
  });
  
  // excludeExpiredProjects 변경 시 프로젝트 다시 로드
  $: if (excludeExpiredProjects !== undefined && typeof excludeExpiredProjects === 'boolean') {
    fetchProjects();
  }
  
  // 검색어와 상태 필터에 따라 프로젝트 필터링
  $: {
    let baseProjectFilter = projects.filter(project => countProjectApplicants(project.id) > 0);
    
    // 검색어 필터링
    if (projectSearchTerm.trim() !== "") {
      const searchLower = projectSearchTerm.toLowerCase();
      baseProjectFilter = baseProjectFilter.filter(project => 
        project.title?.toLowerCase().includes(searchLower) || 
        project.company?.toLowerCase().includes(searchLower) ||
        project.description?.toLowerCase().includes(searchLower)
      );
    }
    
    // 상태 필터링 - 해당 프로젝트의 지원자 중 해당 상태가 있는 프로젝트만 표시
    if (selectedStatusFilter !== 'all') {
      baseProjectFilter = baseProjectFilter.filter(project => {
        return countProjectApplicantsByStatus(project.id, selectedStatusFilter) > 0;
      });
    }
    
    filteredProjects = baseProjectFilter;
  }
  
  // 반응형 로직: 필터링 조건이 변경될 때마다 실행
  $: {
    if (allRawApplicants) {
      // 1. '제외된 회원 포함' 여부에 따라 1차 필터링
      const baseData = showExcludedUsers
        ? allRawApplicants
        : allRawApplicants.filter(app => !isUserExcluded(app.userInfo) && !isUserExcluded(app.raw));

      // 공고별 지원자 수 동적 업데이트
      if (projects.length > 0) {
        const newCounts = {};
        // 필터링된 데이터를 기반으로 프로젝트별 지원자 수를 계산
        baseData.forEach(app => {
          const pid = String(app.projectId);
          if (pid) {
            if (!newCounts[pid]) {
              newCounts[pid] = 0;
            }
            newCounts[pid]++;
          }
        });

        // `projects` 배열에 계산된 지원자 수를 업데이트하여 반응성을 트리거
        projects = projects.map(p => ({
          ...p,
          applicantCount: newCounts[String(p.id)] || 0
        }));
      }

      // 2. '전체 지원자' 탭을 위한 고유 지원자 목록 생성 (projectCount 계산 포함)
      const uniqueApplicantsMap = {};
      baseData.forEach(app => {
        const uniqueKey = app.userId || (app.email && app.email !== '-' ? app.email : app.id);
        if (uniqueKey) {
          if (!uniqueApplicantsMap[uniqueKey]) {
            uniqueApplicantsMap[uniqueKey] = {
              id: app.id,
              userId: app.userId,
              name: app.name,
              email: app.email,
              age: app.age,
              isBlacklist: app.isBlacklist,
              uniqueKey: uniqueKey,
              projectCount: 1,
              unreadCount: (app.is_read === 'N' || !app.is_read) ? 1 : 0,
              userInfo: app.userInfo
            };
          } else {
            uniqueApplicantsMap[uniqueKey].projectCount++;
            if (app.is_read === 'N' || !app.is_read) {
              uniqueApplicantsMap[uniqueKey].unreadCount = (uniqueApplicantsMap[uniqueKey].unreadCount || 0) + 1;
            }
          }
        }
      });
      const uniqueApplicants = Object.values(uniqueApplicantsMap);
      
      // 지원자별 최신 지원일 기준으로 내림차순 정렬
      uniqueApplicants.forEach(applicant => {
        // 해당 지원자의 모든 지원 중 최신 날짜 찾기
        const userApplications = baseData.filter(app => 
          app.userId === applicant.userId || 
          (app.email && app.email !== '-' && app.email === applicant.email)
        );
        
        let latestDate = new Date(0); // 1970년 1월 1일
        userApplications.forEach(app => {
          const appDate = new Date(app.applied_at || app.appliedAt || app.createdAt || app.created_at || app.date || 0);
          if (appDate > latestDate) {
            latestDate = appDate;
          }
        });
        
        applicant.latestApplicationDate = latestDate;
      });
      
      uniqueApplicants.sort((a, b) => {
        const aDate = a.latestApplicationDate || new Date(0);
        const bDate = b.latestApplicationDate || new Date(0);
        return bDate - aDate; // 내림차순
      });
      
      console.log('지원자 정렬 완료:', uniqueApplicants.length, '명, 첫 번째 지원자:', uniqueApplicants[0]?.name, uniqueApplicants[0]?.latestApplicationDate);
      
      allApplicants = uniqueApplicants; // 기존 로직 호환성을 위해 유지

      // 3. '전체 지원자' 탭: 검색어와 상태로 최종 필터링
      let filteredApplicants = uniqueApplicants;
      
      // 검색어 필터링
      if (applicantSearchTerm.trim() !== "") {
        const searchLower = applicantSearchTerm.toLowerCase();
        filteredApplicants = filteredApplicants.filter(applicant => 
          applicant.name?.toLowerCase().includes(searchLower) || 
          applicant.email?.toLowerCase().includes(searchLower)
        );
      }
      
      // 상태 필터링 - 해당 지원자가 지원한 프로젝트 중 해당 상태가 있는 지원자만 표시
      if (selectedStatusFilter !== 'all') {
        filteredApplicants = filteredApplicants.filter(applicant => {
          return hasApplicantStatus(applicant.userId, selectedStatusFilter);
        });
      }
      
      filteredAllApplicants = filteredApplicants;

      // 4. '프로젝트별 지원자' 탭: 선택된 프로젝트에 따라 필터링
      if (selectedProject) {
        let projectApplicants = baseData.filter(app => String(app.projectId) === String(selectedProject.id));
        
        // 상태 필터링 적용
        if (selectedStatusFilter !== 'all') {
          projectApplicants = projectApplicants.filter(app => app.status === parseInt(selectedStatusFilter));
        }
        
        applicants = projectApplicants;
      } else {
        applicants = []; // 선택된 프로젝트가 없으면 빈 목록
      }
    }
  }
  
  // 검색어 초기화 함수
  function clearSearch() {
    if (activeTab === 'projects') {
      projectSearchTerm = "";
    } else {
      applicantSearchTerm = "";
    }
  }
  
  // 이메일 전송 함수 추가
  async function sendEmail(applicant, subject, content) {
    try {
      isSendingEmail = true;
      emailSendError = null;
      emailSendSuccess = false;
      
      const token = localStorage.getItem('token');
      
      // 받는 사람 이메일 결정 (지원자 이메일 또는 기본값)
      const receiverEmail = applicant.email && applicant.email !== '-' 
        ? applicant.email 
        : emailConfig.default_receiver;
      
      // 이메일 보내기 API 호출
      const response = await fetch(`${API_BASE_URL}/api/email/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to: receiverEmail,
          subject: subject,
          text: content,
          html: content.replace(/\n/g, '<br>'),
          from: emailConfig.sender_email,
          sender_password: emailConfig.password
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || '이메일 전송에 실패했습니다.');
      }
      
      console.log(`${applicant.name}에게 이메일 전송 성공!`);
      emailSendSuccess = true;
      
      // 성공 메시지 표시 후 모달 닫기
      setTimeout(() => {
        closeEmailModal();
      }, 2000);
      
      return true;
    } catch (error) {
      console.error('이메일 전송 오류:', error);
      emailSendError = error.message;
      return false;
    } finally {
      isSendingEmail = false;
    }
  }
  
  // 결과 안내 이메일 양식 생성
  function generateEmailTemplate(applicant) {
    // 지원자 이름
    const applicantName = applicant.name || '지원자님';
    
    // 프로젝트 제목
    const projectTitle = applicant.projectTitle || '프로젝트';
    
    // 지원 상태
    const statusObj = statusOptions.find(status => status.id === applicant.status) || statusOptions[0];
    const statusLabel = statusObj.label;
    
    // 이전 상태 (있는 경우)
    const previousStatus = applicant.previousStatus || previousApplicationStatus[applicant.id];
    const previousStatusObj = previousStatus ? 
      statusOptions.find(status => status.id === previousStatus) : null;
    const previousStatusLabel = previousStatusObj ? previousStatusObj.label : null;
    
    // 상태 변경 문구
    let statusChangeText = '';
    if (previousStatusLabel) {
      statusChangeText = `"${previousStatusLabel}"에서 "${statusLabel}"(으)로`;
    } else {
      statusChangeText = `"${statusLabel}"(으)로`;
    }
    
    // 이메일 제목 설정 (상태에 따라 다르게)
    switch (applicant.status) {
      case 3: // accepted
        emailSubject = `[WIDE-WORK] ${projectTitle} 합격 안내`;
        break;
      case 4: // rejected
        emailSubject = `[WIDE-WORK] ${projectTitle} 불합격 안내`;
        break;
      case 5: // project_canceled
        emailSubject = `[WIDE-WORK] ${projectTitle} 취소 안내`;
        break;
      case 6: // project_delayed
        emailSubject = `[WIDE-WORK] ${projectTitle} 연기 안내`;
        break;
      default:
        emailSubject = `[WIDE-WORK] ${projectTitle} 지원 결과 안내`;
    }
    
    // 이메일 내용 설정
    emailContent = `안녕하세요, ${applicantName}님\n\n`;
    
    // 상태별 다른 내용 추가
    switch (applicant.status) {
      case 3: // accepted (합격)
        emailContent += `축하드립니다.\n\n`;
        emailContent += `지원하신 [${projectTitle}] 프로젝트에 합격되신 것을 진심으로 축하드립니다.\n`;
        emailContent += `${applicantName}님의 성공적인 프로젝트 마무리를 기원합니다.\n\n`;
        emailContent += `관련하여 추가 사항은 별도로 안내드리겠습니다.\n`;
        break;
        
      case 4: // rejected (불합격)
        emailContent += `본 프로젝트에 지원하신 ${applicantName}님의 자질과 역량은 뛰어나시지만 아쉽게도 진행이 어렵게 되었습니다.\n`;
        emailContent += `이에 안내 메일 드리오니, 업무에 참고해 주시기 바랍니다.\n\n`;
        emailContent += `참여에 다시 한번 감사 드리며 추후에 다른 프로젝트로 인사 드리겠습니다.\n`;
        break;

      case 10: // price_difficulty (진행 어려움 - 단가)
        emailContent += `${applicantName}님의 역량은 충분히 인정되나, 아쉽게도 단가 협상이 어려워 프로젝트 진행이 어렵게 되었습니다.\n`;
        emailContent += `귀하의 전문성은 높이 평가하며, 향후 더 좋은 조건의 프로젝트로 인사 드릴 수 있기를 바랍니다.\n\n`;
        emailContent += `참여에 다시 한번 감사 드립니다.\n`;
        break;

      case 11: // grade_difficulty (진행 어려움 - 등급)
        emailContent += `${applicantName}님의 경험과 역량은 훌륭하나, 아쉽게도 고객사 요구 등급과의 맞춤이 어려워 프로젝트 진행이 어렵게 되었습니다.\n`;
        emailContent += `귀하의 실력은 충분히 인정되며, 더 적합한 프로젝트로 곧 인사 드릴 수 있기를 바랍니다.\n\n`;
        emailContent += `참여에 다시 한번 감사 드립니다.\n`;
        break;
        
      case 5: // project_canceled (취소)
        emailContent += `저희는 본 프로젝트에 지원하신 ${applicantName}님이 가장 적합하다고 생각하고 있습니다.\n`;
        emailContent += `그런데 아쉽게도 고객사의 프로젝트 진행이 취소 되었습니다.\n`;
        emailContent += `이에 안내 메일 드리오니, 업무에 참고해 주시기 바랍니다.\n\n`;
        emailContent += `참여에 다시 한번 감사 드리며 추후에 보다 좋은 프로젝트로 인사 드리겠습니다.\n`;
        break;
        
      case 6: // project_delayed (연기)
        emailContent += `저희는 본 프로젝트에 지원하신 ${applicantName}님이 가장 적합하다고 생각하고 있습니다.\n`;
        emailContent += `그런데 아쉽게도 고객사가 프로젝트 진행 일정을 계속 연기하고 있어서 저희는 프로젝트 진행이 어렵다고 판단하고 있습니다. 그래서 현 프로젝트를 마감하려고합니다.\n`;
        emailContent += `이에 안내 메일 드리오니, 업무에 참고해 주시기 바랍니다.\n\n`;
        emailContent += `참여에 다시 한번 감사 드리며 추후에 보다 좋은 프로젝트로 인사 드리겠습니다.\n`;
        break;
        
      case 2: // proceeding
        emailContent += `프로젝트가 진행 단계로 전환되었습니다.\n`;
        emailContent += `추가 정보를 위해 담당자가 곧 연락드릴 예정입니다.\n\n`;
        break;
        
      case 7: // closed
        emailContent += `${projectTitle} 프로젝트가 마감되었습니다.\n`;
        emailContent += `아쉽게도 이번 프로젝트에는 다른 지원자가 선정되었습니다.\n`;
        emailContent += `그러나 귀하의 역량을 높이 평가하며, 향후 다른 프로젝트에 지원해 주시기를 바랍니다.\n\n`;
        break;
        
      case 1: // reviewing
        emailContent += `귀하의 지원서가 현재 검토 중입니다.\n`;
        emailContent += `결과는 빠른 시일 내에 알려드리겠습니다.\n\n`;
        break;
    }
    
    emailContent += `감사합니다.\n\n`;
    emailContent += `WIDE-WORK 팀 드림`;
    
    // 이메일 모달 열기
    isEmailModalOpen = true;
  }
  
  // 이메일 모달 닫기
  function closeEmailModal() {
    isEmailModalOpen = false;
    emailSubject = '';
    emailContent = '';
    emailSendError = null;
    emailSendSuccess = false;
  }
  
  // 열람 상태로 변경 기능
  async function markAsRead(applicant) {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/api/applications/${applicant.id}/read-status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          is_read: 'Y'
        })
      });
      
      if (!response.ok) {
        throw new Error('열람 상태 변경에 실패했습니다.');
      }
      
      // 성공 시 모든 관련 데이터 업데이트
      updateApplicationData(applicant.id, 'Y');
      
      console.log(`지원자 ${applicant.name}의 열람 상태가 업데이트되었습니다.`);
      
    } catch (error) {
      console.error('열람 상태 변경 오류:', error);
      // 사용자에게 알리지 않고 조용히 실패
    }
  }
  
  // 데이터 업데이트 통합 함수
  function updateApplicationData(applicationId, readStatus) {
    // 1. applicantProjects 업데이트
    applicantProjects = applicantProjects.map(a => 
      a.id === applicationId ? { ...a, is_read: readStatus } : a
    );
    
    // 2. filteredApplicantProjects 업데이트
    filteredApplicantProjects = filteredApplicantProjects.map(a => 
      a.id === applicationId ? { ...a, is_read: readStatus } : a
    );
    
    // 3. applicants 업데이트 (프로젝트별 지원자 목록)
    applicants = applicants.map(a => 
      a.id === applicationId ? { ...a, is_read: readStatus } : a
    );
    
    // 4. allRawApplicants 업데이트 (전체 데이터)
    allRawApplicants = allRawApplicants.map(a => 
      a.id === applicationId ? { ...a, is_read: readStatus } : a
    );
    
    // 5. 전역 데이터 업데이트
    if (typeof window !== 'undefined' && window.globalApplicantsData) {
      window.globalApplicantsData = window.globalApplicantsData.map(a => 
        a.id === applicationId ? { ...a, is_read: readStatus } : a
      );
    }
    
    // 6. 지원자 수 다시 계산 (미열람 수 업데이트)
    updateUnreadCounts();
    
    // 7. AG Grid 데이터 새로고침
    if (gridProjectApi) {
      gridProjectApi.setRowData(applicantProjects);
    }
    
    console.log(`지원 ID ${applicationId}의 열람 상태가 ${readStatus}로 업데이트되었습니다.`);
  }
  
  // 미열람 수 업데이트 함수
  function updateUnreadCounts() {
    // 지원자별 미열람 수 다시 계산
    if (allRawApplicants && allRawApplicants.length > 0) {
      const baseData = showExcludedUsers
        ? allRawApplicants
        : allRawApplicants.filter(app => !isUserExcluded(app.userInfo) && !isUserExcluded(app.raw));

      const uniqueApplicantsMap = {};
      baseData.forEach(app => {
        const uniqueKey = app.userId || (app.email && app.email !== '-' ? app.email : app.id);
        if (uniqueKey) {
          if (!uniqueApplicantsMap[uniqueKey]) {
            uniqueApplicantsMap[uniqueKey] = {
              id: app.id,
              userId: app.userId,
              name: app.name,
              email: app.email,
              age: app.age,
              isBlacklist: app.isBlacklist,
              uniqueKey: uniqueKey,
              projectCount: 1,
              unreadCount: (app.is_read === 'N' || !app.is_read) ? 1 : 0,
              userInfo: app.userInfo
            };
          } else {
            uniqueApplicantsMap[uniqueKey].projectCount++;
            if (app.is_read === 'N' || !app.is_read) {
              uniqueApplicantsMap[uniqueKey].unreadCount = (uniqueApplicantsMap[uniqueKey].unreadCount || 0) + 1;
            }
          }
        }
      });
      
      const uniqueApplicants = Object.values(uniqueApplicantsMap);
      
      // 지원자별 최신 지원일 기준으로 내림차순 정렬
      uniqueApplicants.forEach(applicant => {
        // 해당 지원자의 모든 지원 중 최신 날짜 찾기
        const userApplications = baseData.filter(app => 
          app.userId === applicant.userId || 
          (app.email && app.email !== '-' && app.email === applicant.email)
        );
        
        let latestDate = new Date(0); // 1970년 1월 1일
        userApplications.forEach(app => {
          const appDate = new Date(app.applied_at || app.appliedAt || app.createdAt || app.created_at || app.date || 0);
          if (appDate > latestDate) {
            latestDate = appDate;
          }
        });
        
        applicant.latestApplicationDate = latestDate;
      });
      
      uniqueApplicants.sort((a, b) => {
        const aDate = a.latestApplicationDate || new Date(0);
        const bDate = b.latestApplicationDate || new Date(0);
        return bDate - aDate; // 내림차순
      });
      
      allApplicants = uniqueApplicants;
      
      // 검색어로 필터링
      if (applicantSearchTerm.trim() === "") {
        filteredAllApplicants = uniqueApplicants;
      } else {
        const searchLower = applicantSearchTerm.toLowerCase();
        filteredAllApplicants = uniqueApplicants.filter(applicant => 
          applicant.name?.toLowerCase().includes(searchLower) || 
          applicant.email?.toLowerCase().includes(searchLower)
        );
      }
    }
  }
  
  // 미열람으로 복구 기능
  async function markAsUnread(selectedApplications) {
    try {
      if (selectedApplications.length === 0) {
        alert('다시 미열람으로 변경할 항목을 선택해주세요.');
        return;
      }
      
      const confirmMessage = `선택한 ${selectedApplications.length}건을 미열람 상태로 복구하시겠습니까?`;
      if (!confirm(confirmMessage)) {
        return;
      }
      
      const token = localStorage.getItem('token');
      let successCount = 0;
      let failCount = 0;
      
      // 선택된 각 지원에 대해 미열람 상태로 변경
      for (const applicationId of selectedApplications) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/applications/${applicationId}/read-status`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ 
              is_read: 'N'
            })
          });
          
          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`지원 ID ${applicationId} 미열람 상태 변경 오류:`, error);
          failCount++;
        }
      }
      
      // 성공 시 선택된 지원들에 대해 데이터 업데이트
      selectedApplications.forEach(applicationId => {
        updateApplicationData(applicationId, 'N');
      });
      
      // 결과 알림
      alert(`${successCount}건이 미열람 상태로 복구되었습니다.${failCount > 0 ? ` ${failCount}건은 실패했습니다.` : ''}`);
      
      // 선택 해제
      if (gridProjectApi) {
        gridProjectApi.deselectAll();
      }
      
      console.log(`${selectedApplications.length}건이 미열람 상태로 복구되었습니다.`);
      
      // 모든 데이터 새로고침
      updateUnreadCounts();
      
    } catch (error) {
      console.error('미열람 상태 복구 오류:', error);
      alert(`미열람 상태 복구 실패: ${error.message}`);
    }
  }
  
  // 탭 변경 함수
  function changeTab(tab) {
    activeTab = tab;

    // 탭 변경 시 선택 초기화
    if (tab === 'projects') {
      selectedApplicantForList = null;
      applicantProjects = [];
      applicantSearchTerm = "";
      // URL을 기본 상태로 복원
      window.history.pushState(
        { page: 'admin-applicants' },
        '',
        '#admin-applicants'
      );
    } else {
      selectedProject = null;
      applicants = [];
      projectSearchTerm = "";
    }

    // 상태 필터 초기화
    selectedStatusFilter = 'all';
  }
  
  // 모든 프로젝트 가져오기
  async function fetchProjects() {
    try {
      isLoading = true;
      loadError = null;
      
      const token = localStorage.getItem('token');
      
      // API 엔드포인트 결정 (관리자와 기업 사용자 구분)
      let apiUrl = `${API_BASE_URL}/api/projects`;
      let params = new URLSearchParams();
      
      // 상태 필터 파라미터 추가
      if (selectedStatusFilter !== 'all') {
        params.append('status', selectedStatusFilter);
      }
      
      // 마감 제외 필터 파라미터 추가
      if (excludeExpiredProjects) {
        params.append('excludeExpired', 'true');
      }
      
      // 기업 사용자인 경우 내 공고만 가져오는 엔드포인트 사용
      if (isCompany && !isAdmin) {
        apiUrl = `${API_BASE_URL}/api/company/projects`;
        
        // 백엔드에 회사 전용 엔드포인트가 없는 경우를 대비해 userID 파라미터 추가
        if (user && user.id) {
          params.append('userId', user.id);
        }
      }
      
      // 파라미터가 있으면 URL에 추가
      if (params.toString()) {
        apiUrl += `?${params.toString()}`;
      }
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('프로젝트 목록을 불러오는 데 실패했습니다.');
      }
      
      const data = await response.json();
      
      // API 응답이 배열이 아닌 경우 처리 (data.projects와 같은 하위 객체일 수 있음)
      let allProjects = Array.isArray(data) ? data : 
                (data.projects ? data.projects : 
                (data.data ? data.data : []));
      
      // 백엔드에서 필터링이 적용되지 않았다면 프론트엔드에서 필터링
      if (isCompany && !isAdmin && user && user.id) {
        allProjects = allProjects.filter(project => 
          project.userId === user.id || 
          project.company_id === user.id || 
          project.companyId === user.id || 
          project.company === user.company ||
          project.createdBy === user.id
        );
      }
      
      // 프로젝트 목록 저장 (임시)
      const tempProjects = allProjects;
      
      // 각 프로젝트의 지원자 수 가져오기
      try {
        const countsResponse = await fetch(`${API_BASE_URL}/api/projects/applicants/counts`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (countsResponse.ok) {
          const countsData = await countsResponse.json();
          console.log("프로젝트별 지원자 수 데이터:", countsData);
          
          // 프로젝트 객체에 지원자 수 추가
          if (countsData && countsData.counts) {
            allProjects = tempProjects.map(project => {
              const projectId = String(project.id);
              return {
                ...project,
                applicantCount: countsData.counts[projectId] || 0
              };
            });
          }
        } else {
          console.warn("프로젝트별 지원자 수 조회 실패, 가짜 데이터 사용");
          // API 호출 실패 시 간단한 가짜 데이터 사용
          allProjects = tempProjects.map(project => {
            // 프로젝트 ID를 이용한 고정값
            const idNum = parseInt(String(project.id).replace(/\D/g, '')) || 0;
            return {
              ...project,
              applicantCount: (idNum % 10) + 1 // 1~10 사이의 값
            };
          });
        }
      } catch (error) {
        console.warn("지원자 수 조회 오류, 가짜 데이터 사용:", error);
        // 오류 발생 시 간단한 가짜 데이터 사용
        allProjects = tempProjects.map(project => {
          // 프로젝트 ID를 이용한 고정값
          const idNum = parseInt(String(project.id).replace(/\D/g, '')) || 0;
          return {
            ...project,
            applicantCount: (idNum % 10) + 1 // 1~10 사이의 값
          };
        });
      }
      
      // updated_at 기준으로 내림차순 정렬 (최신순)
      allProjects.sort((a, b) => {
        const aDate = new Date(a.updated_at || a.updatedAt || a.created_at || a.createdAt || a.date || a.createdOn || a.lastModified || 0);
        const bDate = new Date(b.updated_at || b.updatedAt || b.created_at || b.createdAt || b.date || b.createdOn || b.lastModified || 0);
        return bDate - aDate;
      });
      
      console.log('프로젝트 정렬 완료:', allProjects.length, '개, 첫 번째 프로젝트:', allProjects[0]?.title, allProjects[0]?.updated_at || allProjects[0]?.created_at);
      
      projects = allProjects;
      filteredProjects = allProjects; // 초기 필터링된 프로젝트 목록 설정
      console.log("프로젝트 목록 로드 성공:", projects);
      
    } catch (error) {
      console.error('프로젝트 로딩 오류:', error);
      loadError = error.message;
      projects = [];
      filteredProjects = [];
    } finally {
      isLoading = false;
    }
  }
  
  // 모든 지원자 목록 가져오기
  async function fetchAllApplicants() {
    try {
      isLoading = true;
      loadError = null;
      
      const token = localStorage.getItem('token');
      
      // 슈퍼 관리자인 경우 전용 API 호출
      if (isSuperAdmin) {
        try {
          console.log("슈퍼 관리자 권한으로 모든 지원자 정보 요청");
          const response = await fetch(`${API_BASE_URL}/api/applications/admin`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log("슈퍼 관리자용 지원자 데이터 로드 성공");
            
            let applications = [];
            if (data && data.applications) {
              applications = data.applications;
              console.log(`슈퍼 관리자 API 응답: ${applications.length}개의 지원 데이터 수신`);
              
              if (applications.length > 0) {
                // 사용자 정보 가져오기
                const users = await fetchUsersData();
                console.log(`사용자 정보: ${users.length}명 로드됨`);
                
                // 데이터 처리
                await processApplicationsWithUsers(applications, users);
                return;
              }
            }
          } else {
            console.error("슈퍼 관리자 API 응답 실패:", response.status);
          }
        } catch (error) {
          console.error("슈퍼 관리자 지원자 목록 로드 실패:", error);
          // 실패하면 일반 관리자 방식으로 계속 진행
        }
      }
      
      // 일반 관리자인 경우 모든 지원자 정보 가져오기
      if (isAdmin) {
        try {
          console.log("관리자 권한으로 모든 지원자 정보 요청");
          const response = await fetch(`${API_BASE_URL}/api/applications/all`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log("모든 지원자 데이터 로드 성공");
            
            let applications = [];
            if (data && data.applications) {
              applications = data.applications;
              console.log(`관리자 API 응답: ${applications.length}개의 지원 데이터 수신`);
            } else if (Array.isArray(data)) {
              applications = data;
              console.log(`관리자 API 응답: ${applications.length}개의 지원 데이터 수신 (배열)`);
            } else {
              console.warn("관리자 API 응답에 applications 필드가 없습니다:", data);
            }
            
            if (applications.length > 0) {
              // 사용자 정보 가져오기
              const users = await fetchUsersData();
              console.log(`사용자 정보: ${users.length}명 로드됨`);
              
              // 데이터 처리
              await processApplicationsWithUsers(applications, users);
              return;
            } else {
              console.warn("관리자 API에서 지원 데이터가 없습니다.");
            }
          } else {
            console.error("관리자 API 응답 실패:", response.status);
          }
        } catch (error) {
          console.error("관리자 지원자 목록 로드 실패:", error);
          // 실패하면 일반 방식으로 계속 진행
        }
      }
      
      // 일반적인 방식으로 계속 진행 (관리자가 아니거나 관리자 API가 실패한 경우)
      // 먼저 모든 사용자 정보 가져오기
      const users = await fetchUsersData();
      
      // 모든 지원 정보 가져오기
      const applications = await fetchApplicationsData();
      
      if (applications && applications.length > 0) {
        // 두 데이터 합치기
        await processApplicationsWithUsers(applications, users);
      } else {
        // 지원 정보가 없는 경우 빈 배열로 처리
        applicants = [];
        allApplicants = [];
        filteredAllApplicants = [];
      }
    } catch (error) {
      console.error('전체 지원자 목록 로딩 오류:', error);
      loadError = error.message;
      applicants = [];
      allApplicants = [];
      filteredAllApplicants = [];
    } finally {
      isLoading = false;
    }
  }
  
  // 사용자 정보 가져오기 (users 테이블에서)
  async function fetchUsersData() {
    try {
      const token = localStorage.getItem('token');
      
      // 여러 엔드포인트 시도
      const endpoints = [
        `${API_BASE_URL}/api/users`,           // 새로 추가한 전체 사용자 API
        `${API_BASE_URL}/api/users/all`,       // 모든 사용자
        `${API_BASE_URL}/api/admin/users`      // 관리자용
      ];
      
      let response = null;
      let data = null;
      let success = false;
      
      // 엔드포인트 순서대로 시도
      for (const endpoint of endpoints) {
        try {
          console.log("사용자 정보 API 시도:", endpoint);
          response = await fetch(endpoint, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            data = await response.json();
            console.log("사용자 정보 API 응답 성공:", endpoint);
            success = true;
            break;
          }
        } catch (err) {
          console.warn(`사용자 정보 API 실패: ${endpoint}`, err);
        }
      }
      
      if (!success) {
        console.warn("모든 사용자 정보 API 엔드포인트 실패");
        
        // 개발 중 테스트 목적 - 지원자 데이터에서 사용자 정보 추출
        try {
          // 지원자 목록 API 호출
          const appResponse = await fetch(`${API_BASE_URL}/api/applications`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (appResponse.ok) {
            const appData = await appResponse.json();
            console.log("지원자 API에서 사용자 정보 추출 시도");
            
            // 지원자 데이터에서 사용자 정보 추출
            let applications = [];
            if (Array.isArray(appData)) {
              applications = appData;
            } else if (appData.applications) {
              applications = appData.applications;
            } else if (appData.data) {
              if (Array.isArray(appData.data)) {
                applications = appData.data;
              } else if (appData.data.applications) {
                applications = appData.data.applications;
              }
            }
            
            // 중복 제거된 사용자 정보 생성
            const uniqueUsers = {};
            applications.forEach(app => {
              const userId = app.userId || app.user_id;
              if (userId && !uniqueUsers[userId]) {
                uniqueUsers[userId] = {
                  id: userId,
                  name: app.name || app.userName || app.user?.name || app.user_name,
                  email: app.email || app.user?.email || app.user_email || '',
                  age: app.age || app.user?.age || '',
                  birth_date: app.birth_date || app.user?.birth_date || app.birthDate || app.user?.birthDate || '',
                  is_blacklist: app.is_blacklist || app.user?.is_blacklist || 'N',
                  career_year: app.career_year || app.user?.career_year || app.careerYear || app.user?.careerYear || null,
                  career_month: app.career_month || app.user?.career_month || app.careerMonth || app.user?.careerMonth || null
                };
              }
            });
            
            const extractedUsers = Object.values(uniqueUsers);
            console.log("지원자 데이터에서 추출된 사용자 정보:", extractedUsers.length);
            
            return extractedUsers.length > 0 ? extractedUsers : [];
          }
        } catch (err) {
          console.warn("지원자 API에서 사용자 정보 추출 실패:", err);
        }
        
        // 모든 방법이 실패하면 빈 배열 반환
        return [];
      }
      
      // 데이터 추출
      let users = [];
      if (Array.isArray(data)) {
        users = data;
      } else if (data.users) {
        users = data.users;
      } else if (data.data) {
        if (Array.isArray(data.data)) {
          users = data.data;
        } else if (data.data.users) {
          users = data.data.users;
        }
      }
      
      console.log("추출된 사용자 정보:", users.length);
      return users;
    } catch (error) {
      console.error("사용자 정보 가져오기 오류:", error);
      return [];
    }
  }
  
  // 지원 정보 가져오기
  async function fetchApplicationsData() {
    try {
      const token = localStorage.getItem('token');
      
      // 엔드포인트 목록 (여러 API를 순서대로 시도)
      const endpoints = [
        `${API_BASE_URL}/api/applications/all`,  // 첫번째 시도 
        `${API_BASE_URL}/api/applications`,      // 두번째 시도
        `${API_BASE_URL}/api/users/applications`, // 세번째 시도
        `${API_BASE_URL}/api/admin/applications` // 네번째 시도 (관리자용)
      ];
      
      // 파라미터 구성
      let params = new URLSearchParams();
      
      // 기업 사용자인 경우 회사 ID 파라미터 추가
      if (isCompany && !isAdmin && user && user.id) {
        params.append('companyId', user.id);
      }
      
      // 상태 필터 파라미터 추가
      if (selectedStatusFilter !== 'all') {
        params.append('status', selectedStatusFilter);
      }
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      let response = null;
      let data = null;
      let success = false;
      
      // 여러 엔드포인트를 순서대로 시도
      for (const baseUrl of endpoints) {
        const apiUrl = baseUrl + queryString;
        console.log("지원자 목록 API 시도:", apiUrl);
        
        try {
          response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            data = await response.json();
            console.log("API 응답 성공:", apiUrl);
            success = true;
            break; // 성공하면 루프 종료
          } else if (response.status === 401 || response.status === 403) {
            // 인증 오류 시 즉시 처리하고 루프 중단
            handleApiResponse(response);
            return;
          }
        } catch (err) {
          console.warn(`API 엔드포인트 실패: ${apiUrl}`, err);
          // 다음 엔드포인트로 계속 시도
        }
      }
      
      if (!success || !data) {
        throw new Error('지원자 목록을 불러오는 데 실패했습니다. 모든 API 엔드포인트 시도 실패.');
      }
      
      // 다양한 응답 구조 처리
      let applications = [];
      if (Array.isArray(data)) {
        applications = data;
      } else if (typeof data === 'object') {
        if (data.applications) applications = data.applications;
        else if (data.applicants) applications = data.applicants;
        else if (data.data) {
          if (Array.isArray(data.data)) {
            applications = data.data;
          } else if (typeof data.data === 'object') {
            if (data.data.applications) applications = data.data.applications;
            else if (data.data.applicants) applications = data.data.applicants;
            else if (data.data.items) applications = data.data.items;
          }
        }
        else if (data.items) applications = data.items;
        else if (data.results) applications = data.results;
        else if (data.users) applications = data.users;
      }
      
      console.log("추출된 지원자 데이터:", applications);
      
      return applications;
    } catch (error) {
      console.error('지원 정보 로딩 오류:', error);
      return [];
    }
  }
  
  // 지원 정보와 사용자 정보 합치기
  async function processApplicationsWithUsers(applications, users) {
    console.log("지원 정보와 사용자 정보 합치기:", applications.length, "지원,", users.length, "사용자");

    // 사용자 정보를 ID로 쉽게 접근할 수 있도록 맵 생성
    const userMap = {};
    if (users && users.length > 0) {
      users.forEach(user => {
        const userId = String(user.id || user._id || '');
        if (userId) {
          userMap[userId] = user;
        }
      });

      console.log("사용자 정보 맵:", Object.keys(userMap).length);
    } else {
      console.log("사용자 정보 없음, 지원 데이터만 사용합니다.");
    }

    // 프로젝트 IDs 추출
    const projectIds = [...new Set(applications.map(app => {
      const id = app.projectId || app.project_id;
      if (!id) {
        console.warn("프로젝트 ID가 없는 지원 데이터:", app);
      }
      return id;
    }))].filter(id => id);

    console.log(`추출된 프로젝트 ID: ${projectIds.length}개`);

    // 프로젝트 정보 가져오기
    try {
      const projects = await fetchProjectsData(projectIds);
      const projectMap = {};
      projects.forEach(project => {
        const projectId = String(project.id || project._id);
        if (projectId) {
          projectMap[projectId] = project;
        }
      });
      
      console.log("프로젝트 정보 맵:", Object.keys(projectMap).length);
      
      // 데이터 매핑 및 정제
      const mappedApplicants = applications.map(app => {
        // 지원자 ID 추출
        let appId = String(app.id || app._id || app.applicationId || '');
        // 사용자 ID 추출
        let userId = String(app.userId || app.user_id || app.user?.id || '');
        // 프로젝트 ID 추출
        let projectId = String(app.projectId || app.project_id || app.project?.id || '');
        
        // 해당 사용자 정보 가져오기
        const userInfo = userId && userMap[userId] ? userMap[userId] : null;
        
        // 해당 프로젝트 정보 가져오기
        const projectInfo = projectId && projectMap[projectId] ? projectMap[projectId] : null;
        
        // 이름 - 우선순위: users 테이블 > application 데이터
        const userName = userInfo?.name || 
                        app.user_name || app.name || app.userName || app.applicantName || 
                        app.user?.name;
        
        // 이름이 없는 경우에만 ID로 대체
        let formattedName = userName;
        if (!formattedName || formattedName.trim() === '') {
          const shortId = appId ? (appId.length <= 5 ? appId : appId.slice(-5)) : '';
          formattedName = userId ? `지원자 #${userId}` : (shortId ? `지원자 #${shortId}` : '이름 없음');
        }
        
        // 나이 - 우선순위: users 테이블 > application 데이터
        // 1순위: users 테이블 또는 application 데이터에 age 필드가 있는 경우 사용
        // 2순위: birth_date 필드로부터 나이 계산
        const birthDateStr = userInfo?.birth_date || userInfo?.birthDate || app.birth_date || app.birthDate || app.user?.birth_date;
        let userAge = userInfo?.age || userInfo?.userAge || app.age || app.user?.age;
        
        // 나이가 없으면 생년월일로 계산
        if (!userAge && birthDateStr) {
          userAge = calculateAge(birthDateStr);
        }
        
        // 나이가 여전히 없으면 빈 문자열 또는 '-'로 설정
        if (!userAge || userAge === 0) {
          userAge = '';
        }
        
        // 이메일 - 우선순위: users 테이블 > application 데이터
        const userEmail = userInfo?.email || app.user_email || app.email || app.user?.email || '-';
        
        // 블랙리스트 상태 처리
        const isBlacklist = 
          // DB에서 가져온 값 확인 (문자열 'Y' 또는 'N')
          app.is_blacklist === 'Y' || 
          userInfo?.is_blacklist === 'Y' || 
          // 사용자 테이블에서 가져온 값
          app.user_is_blacklist === 'Y' ||
          // 또는 불리언 값으로 설정된 경우
          app.isBlacklist === true || 
          userInfo?.isBlacklist === true;
        
        // 상태값 처리 - 숫자로 변환
        let status = app.status;
        if (typeof status === 'string') {
          // 문자열 상태값을 숫자로 변환
          status = statusMapping[status] !== undefined ? statusMapping[status] : 0;
        } else if (status === undefined || status === null) {
          status = 0; // 기본값: applied
        }
        
        return {
          id: appId,
          userId: userId,
          name: formattedName,
          projectId: projectId,
          projectTitle: projectInfo?.title || 
                           app.project_title || app.projectTitle || 
                           app.project?.title || app.projectName || '프로젝트명 없음',
          email: userEmail,
          status: status,
          applied_at: app.applied_at || app.appliedAt || app.createdAt || app.created_at || app.date || new Date().toISOString(),
          age: userAge || '-',
          price: formatPrice(app.price),
          available_date: app.available_date || userInfo?.available_date || null, // 면접가능일
          start_work_date: app.start_work_date || userInfo?.start_work_date || null, // 출근가능일
          isBlacklist: isBlacklist, // 블랙리스트 상태 추가
          is_read: app.is_read || 'N', // 열람/미열람 상태 추가
          hasFiles: app.hasFiles ?? app.has_files ?? userInfo?.hasFiles ?? false, // 첨부 파일 여부 추가
          // 원본 데이터도 저장
          raw: app,
          // 사용자 정보 원본 (경력 정보 추가)
          userInfo: userInfo ? {
            ...userInfo,
            // 만약 userInfo에 경력 정보가 없다면 app에서 가져오기
            career_year: userInfo.career_year ?? userInfo.careerYear ?? app.career_year ?? app.careerYear ?? null,
            career_month: userInfo.career_month ?? userInfo.careerMonth ?? app.career_month ?? app.careerMonth ?? null
          } : null,
          // 프로젝트 정보 원본
          projectInfo: projectInfo
        };
      });
      
      console.log("매핑된 지원자 데이터:", mappedApplicants.length);
      
      // 전역 변수와 Svelte 반응형 변수에 데이터 저장
      if (typeof window !== 'undefined') {
        window.globalApplicantsData = mappedApplicants;
      }
      allRawApplicants = mappedApplicants; // 이 할당이 반응형 블록을 트리거
      
      // is_excluded=true 사용자는 제외
      const filteredMappedApplicants = showExcludedUsers ? 
        mappedApplicants : 
        mappedApplicants.filter(app => !isUserExcluded(app.userInfo) && !isUserExcluded(app.raw));
      console.log("제외된 사용자 필터 후:", filteredMappedApplicants.length);
      
      // 현재 선택된 프로젝트에 대한 지원자 목록 업데이트
      if (selectedProject) {
        applicants = filteredMappedApplicants.filter(app => app.projectId === String(selectedProject.id));
      } else {
        applicants = filteredMappedApplicants;
      }

    } catch (error) {
      console.error("프로젝트 데이터 처리 중 오류:", error);
      // 오류 발생시 빈 배열로 설정
      applicants = [];
      allApplicants = [];
      filteredAllApplicants = [];
      allRawApplicants = [];
    }
  }
  
  // 프로젝트 정보 가져오기
  async function fetchProjectsData(projectIds) {
    if (!projectIds || projectIds.length === 0) {
      return [];
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // 여러 엔드포인트 시도
      const endpoints = [
        `${API_BASE_URL}/api/projects`,
        `${API_BASE_URL}/api/admin/projects`,
        `${API_BASE_URL}/api/company/projects`
      ];
      
      let response = null;
      let data = null;
      let success = false;
      
      // 엔드포인트 순서대로 시도
      for (const endpoint of endpoints) {
        try {
          console.log("프로젝트 정보 API 시도:", endpoint);
          response = await fetch(endpoint, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            data = await response.json();
            console.log("프로젝트 정보 API 응답 성공:", endpoint);
            success = true;
            break;
          }
        } catch (err) {
          console.warn(`프로젝트 정보 API 실패: ${endpoint}`, err);
        }
      }
      
      if (!success) {
        console.warn("모든 프로젝트 정보 API 엔드포인트 실패");
        return [];
      }
      
      // 데이터 추출
      let projects = [];
      if (Array.isArray(data)) {
        projects = data;
      } else if (data.projects) {
        projects = data.projects;
      } else if (data.data) {
        if (Array.isArray(data.data)) {
          projects = data.data;
        } else if (data.data.projects) {
          projects = data.data.projects;
        }
      }
      
      // 필요한 프로젝트만 필터링
      const filteredProjects = projectIds.length > 0
        ? projects.filter(project => projectIds.includes(String(project.id || project._id)))
        : projects;
      
      console.log("추출된 프로젝트 정보:", filteredProjects.length);
      return filteredProjects;
    } catch (error) {
      console.error("프로젝트 정보 가져오기 오류:", error);
      return [];
    }
  }
  
  // 선택된 지원자의 지원 프로젝트 목록 가져오기
  async function fetchApplicantProjects(applicant) {
    if (!applicant || !applicant.userId) {
      console.error("지원자 정보 또는 userId가 없습니다:", applicant);
      applicantProjects = [];
      return;
    }
    
    try {
      console.log("지원자 프로젝트 목록 가져오기 시작:", applicant);
      const token = localStorage.getItem('token');
      const userId = applicant.userId;
      
      // 상태 필터 파라미터 구성
      let statusParam = '';
      if (selectedStatusFilter !== 'all') {
        statusParam = `status=${selectedStatusFilter}`;
      }
      
      // API 엔드포인트 시도 (여러 가능한 형식)
      // 가장 신뢰할 수 있는 엔드포인트를 먼저 시도
      const baseEndpoints = [
        `${API_BASE_URL}/api/users/${userId}/applications`, // 새로 추가한 전용 엔드포인트
        `${API_BASE_URL}/api/applications/admin`,          // 슈퍼 관리자용 API (모든 지원 정보)
        `${API_BASE_URL}/api/applications/all`,            // 일반 관리자용 API (모든 지원 정보)
        `${API_BASE_URL}/api/applications?userId=${userId}`,
        `${API_BASE_URL}/api/applications`                 // 마지막 대안
      ];
      
      // 상태 필터 파라미터 추가
      const endpoints = baseEndpoints.map(endpoint => {
        if (statusParam) {
          const separator = endpoint.includes('?') ? '&' : '?';
          return `${endpoint}${separator}${statusParam}`;
        }
        return endpoint;
      });
      
      let response = null;
      let data = null;
      let success = false;
      let successEndpoint = '';
      
      // 여러 엔드포인트를 순서대로 시도
      for (const endpoint of endpoints) {
        try {
          console.log("지원자별 프로젝트 API 시도:", endpoint);
          response = await fetch(endpoint, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            data = await response.json();
            console.log("API 응답 성공:", endpoint);
            success = true;
            successEndpoint = endpoint;
            break;
          } else {
            console.warn(`API 응답 실패 (${response.status}):`, endpoint);
          }
        } catch (err) {
          console.warn(`API 엔드포인트 실패: ${endpoint}`, err);
        }
      }
      
      if (success && data) {
        console.log(`성공한 엔드포인트: ${successEndpoint}, 응답 데이터:`, data);
        
        // 데이터 추출 (다양한 응답 구조 처리)
        let userApplications = [];
        if (Array.isArray(data)) {
          userApplications = data;
          console.log("응답이 배열 형태입니다.");
        } else if (data.applications) {
          userApplications = data.applications;
          console.log("응답에 applications 필드가 있습니다.");
        } else if (data.projects) {
          userApplications = data.projects;
          console.log("응답에 projects 필드가 있습니다.");
        } else if (data.data) {
          if (Array.isArray(data.data)) {
            userApplications = data.data;
            console.log("응답의 data 필드가 배열입니다.");
          } else if (data.data.applications) {
            userApplications = data.data.applications;
            console.log("응답의 data.applications 필드를 사용합니다.");
          } else if (data.data.projects) {
            userApplications = data.data.projects;
            console.log("응답의 data.projects 필드를 사용합니다.");
          }
        } else {
          console.warn("알 수 없는 응답 구조:", data);
        }
        
        // 특정 사용자의 지원만 필터링 (전체 지원 데이터를 가져온 경우)
        if (successEndpoint.includes('/api/applications/admin') || 
            successEndpoint.includes('/api/applications/all') || 
            successEndpoint === `${API_BASE_URL}/api/applications`) {
          console.log("전체 지원 데이터에서 특정 사용자 필터링:", userId);
          const beforeCount = userApplications.length;
          
          userApplications = userApplications.filter(app => {
            const appUserId = String(app.userId || app.user_id);
            const targetUserId = String(userId);
            const isMatch = appUserId === targetUserId;
            
            if (isMatch) {
              console.log(`일치하는 지원 데이터 발견: ID=${app.id}, 사용자ID=${appUserId}`);
            }
            
            return isMatch;
          });
          
          console.log(`필터링 결과: ${beforeCount} -> ${userApplications.length}`);
        }
        
        console.log(`지원자 ${applicant.name}의 지원 데이터:`, userApplications.length);
        
        // 프로젝트 정보 가져오기
        const projectIds = [...new Set(userApplications.map(app => {
          const id = app.projectId || app.project_id;
          if (!id) {
            console.warn("프로젝트 ID가 없는 지원 데이터:", app);
          }
          return id;
        }))].filter(id => id);
        
        console.log(`지원자 ${applicant.name}의 프로젝트 ID:`, projectIds);
        
        if (projectIds.length === 0) {
          console.warn("프로젝트 ID가 없습니다. 빈 결과를 반환합니다.");
          applicantProjects = [];
          return;
        }
        
        const projects = await fetchProjectsData(projectIds).catch(err => {
          console.error("프로젝트 정보 가져오기 실패:", err);
          return [];
        });
        console.log(`지원자 ${applicant.name}의 프로젝트 정보:`, projects.length);
        
        // 프로젝트 맵 생성
        const projectMap = {};
        projects.forEach(project => {
          const projectId = String(project.id || project._id);
          if (projectId) {
            projectMap[projectId] = project;
          }
        });
        
        // 데이터 매핑
        applicantProjects = userApplications.map(app => {
          // 지원 ID 추출
          let appId = String(app.id || app._id || app.applicationId || '');
          // 프로젝트 ID 추출
          let projectId = String(app.projectId || app.project_id || app.project?.id || '');
          
          // 해당 프로젝트 정보 가져오기
          const projectInfo = projectId && projectMap[projectId] ? projectMap[projectId] : null;
          
          if (!projectInfo) {
            console.warn(`프로젝트 정보를 찾을 수 없음: ID=${projectId}`);
          }
          
          // 프로젝트 제목
          const projectTitle = projectInfo?.title || 
                             app.project_title || app.projectTitle || 
                             app.project?.title || app.projectName || '프로젝트명 없음';
          
          // 블랙리스트 상태 처리
          const isBlacklist = 
            // DB에서 가져온 값 확인 (문자열 'Y' 또는 'N')
            app.is_blacklist === 'Y' || 
            applicant.userInfo?.is_blacklist === 'Y' || 
            // 사용자 테이블에서 가져온 값
            app.user_is_blacklist === 'Y' ||
            // 또는 불리언 값으로 설정된 경우
            app.isBlacklist === true || 
            applicant.userInfo?.isBlacklist === true;
          
          // 상태값 처리 - 숫자로 변환
          let status = app.status;
          if (typeof status === 'string') {
            // 문자열 상태값을 숫자로 변환
            status = statusMapping[status] !== undefined ? statusMapping[status] : 0;
          } else if (status === undefined || status === null) {
            status = 0; // 기본값: applied
          }
          
          return {
            id: appId,
            userId: userId,
            name: applicant.name,
            email: applicant.email,
            projectId: projectId,
            projectTitle: projectTitle,
            status: status,
            applied_at: app.applied_at || app.appliedAt || app.createdAt || app.created_at || app.date || new Date().toISOString(),
            price: formatPrice(app.price),
            available_date: app.available_date || applicant.userInfo?.available_date || null, // 면접가능일
            start_work_date: app.start_work_date || applicant.userInfo?.start_work_date || null, // 출근가능일
            isBlacklist: isBlacklist,  // 명시적으로 블랙리스트 상태 추가
            is_read: app.is_read || 'N', // 열람/미열람 상태 추가
            // 원본 데이터도 저장
            raw: app,
            // 사용자 정보 원본
            userInfo: applicant.userInfo,
            // 프로젝트 정보 원본
            projectInfo: projectInfo
          };
        });
        
        // 제외된 사용자(is_excluded) 필터링
        applicantProjects = showExcludedUsers ? 
          applicantProjects : 
          applicantProjects.filter(app => !isUserExcluded(app.userInfo) && !isUserExcluded(app.raw));
        console.log(`지원자 ${applicant.name}이(가) 지원한 프로젝트 (API):`, applicantProjects.length);
        
        // 상태 필터링 적용
        let filteredProjects = applicantProjects;
        if (selectedStatusFilter !== 'all') {
          filteredProjects = filteredProjects.filter(app => app.status === parseInt(selectedStatusFilter));
        }
        
        // 필터링된 프로젝트 목록 초기화
        filteredApplicantProjects = filteredProjects;
      } else {
        console.error(`지원자 ${applicant.name}의 프로젝트 가져오기 실패. 모든 API 엔드포인트 시도 실패.`);
        applicantProjects = [];
        filteredApplicantProjects = [];
      }
    } catch (error) {
      console.error('지원자별 프로젝트 가져오기 오류:', error);
      applicantProjects = [];
      filteredApplicantProjects = [];
      throw error;
    }
  }
  
  // 상태 필터링 함수 추가
  function filterApplicantProjectsByStatus() {
    if (selectedStatusFilter === 'all') {
      filteredApplicantProjects = applicantProjects;
    } else {
      filteredApplicantProjects = applicantProjects.filter(project => project.status === selectedStatusFilter);
    }
  }
  
  // 프로젝트 선택 함수 추가
  function selectProject(project) {
    selectedProject = project;
    
    // 먼저 전체 지원자 데이터에서 필터링 시도 (allApplicants와 applicants 모두 확인)
    if (project && project.id) {
      const projectId = String(project.id);
      
      // 전체 지원자 데이터에서 해당 프로젝트의 지원자 찾기
      let projectApplicants = [];
      
      // 1. 기존 applicants 배열에서 찾기
      const fromApplicants = applicants.filter(app => 
        app.projectId === projectId ||
        (app.projectInfo && String(app.projectInfo.id) === projectId)
      );
      
      // 2. 전체 지원자 데이터에서 찾기 (초기 로드된 데이터 활용)
      if (fromApplicants.length === 0 && window.globalApplicantsData) {
        const fromGlobalData = window.globalApplicantsData.filter(app => 
          app.projectId === projectId ||
          (app.projectInfo && String(app.projectInfo.id) === projectId)
        );
        projectApplicants = fromGlobalData;
      } else {
        projectApplicants = fromApplicants;
      }
      
      // 필터링된 지원자가 있으면 그대로 사용
      if (projectApplicants.length > 0) {
        applicants = projectApplicants;
        console.log(`프로젝트 ${project.title}의 지원자 (로컬 데이터):`, applicants);
      } else {
        // 없으면 API에서 다시 가져오기 시도
        fetchProjectApplicants(project);
      }
    } else {
      applicants = [];
    }
  }
  
  // 프로젝트별 지원자 목록 가져오기
  async function fetchProjectApplicants(project) {
    try {
      isLoading = true;
      loadError = null;
      const token = localStorage.getItem('token');
      const projectId = String(project.id);
      
      // API 엔드포인트 시도 (여러 가능한 형식)
      const endpoints = [
        `${API_BASE_URL}/api/projects/${projectId}/applications`,
        `${API_BASE_URL}/api/applications?projectId=${projectId}`,
        `${API_BASE_URL}/api/projects/${projectId}/applicants`
      ];
      
      let response = null;
      let data = null;
      let success = false;
      
      // 여러 엔드포인트를 순서대로 시도
      for (const endpoint of endpoints) {
        try {
          console.log("프로젝트별 지원자 API 시도:", endpoint);
          response = await fetch(endpoint, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            data = await response.json();
            console.log("API 응답 성공:", endpoint);
            success = true;
            break;
          }
        } catch (err) {
          console.warn(`API 엔드포인트 실패: ${endpoint}`, err);
        }
      }
      
      if (success && data) {
        // 데이터 추출 (다양한 응답 구조 처리)
        let projectApplicants = [];
        if (Array.isArray(data)) {
          projectApplicants = data;
        } else if (data.applications) {
          projectApplicants = data.applications;
        } else if (data.applicants) {
          projectApplicants = data.applicants;
        } else if (data.data) {
          if (Array.isArray(data.data)) {
            projectApplicants = data.data;
          } else if (data.data.applications) {
            projectApplicants = data.data.applications;
          } else if (data.data.applicants) {
            projectApplicants = data.data.applicants;
          }
        }
        
        // 사용자 정보 가져오기
        const users = await fetchUsersData().catch(err => {
          console.warn("사용자 정보 가져오기 실패:", err);
          return []; // 사용자 정보 가져오기 실패 시 빈 배열 반환
        });
        
        // 데이터 매핑 처리
        if (projectApplicants.length > 0) {
          // 사용자 정보를 ID로 쉽게 접근할 수 있도록 맵 생성
          const userMap = {};
          if (users && users.length > 0) {
            users.forEach(user => {
              const userId = String(user.id || user._id || '');
              if (userId) {
                userMap[userId] = user;
              }
            });
          }
          
          // 데이터 매핑
          applicants = projectApplicants.map(app => {
            // 지원자 ID 추출
            let appId = String(app.id || app._id || app.applicationId || '');
            // 사용자 ID 추출
            let userId = String(app.userId || app.user_id || app.user?.id || '');
            
            // 해당 사용자 정보 가져오기
            const userInfo = userId && userMap[userId] ? userMap[userId] : null;
            
            // 이름 - 우선순위: users 테이블 > application 데이터
            const userName = userInfo?.name || userInfo?.userName || 
                            app.name || app.userName || app.applicantName || 
                            app.user?.name || app.user_name;
            
            // 이름이 없는 경우 ID로 대체
            const shortId = appId ? (appId.length <= 5 ? appId : appId.slice(-5)) : '';
            const formattedName = userName || (userId ? `사용자 #${userId}` : (shortId ? `지원자 #${shortId}` : '이름 없음'));
            
            // 나이 - 우선순위: users 테이블 > application 데이터
            // 1순위: users 테이블 또는 application 데이터에 age 필드가 있는 경우 사용
            // 2순위: birth_date 필드로부터 나이 계산
            const birthDateStr = userInfo?.birth_date || userInfo?.birthDate || app.birth_date || app.birthDate || app.user?.birth_date;
            const userAge = userInfo?.age || userInfo?.userAge || app.age || app.user?.age || (birthDateStr ? calculateAge(birthDateStr) : '');
            
            // 이메일 - 우선순위: users 테이블 > application 데이터
            const userEmail = userInfo?.email || app.user_email || app.email || app.user?.email || '-';
            
            // 블랙리스트 상태 처리
            const isBlacklist = 
              // DB에서 가져온 값 확인 (문자열 'Y' 또는 'N')
              app.is_blacklist === 'Y' || 
              userInfo?.is_blacklist === 'Y' || 
              // 사용자 테이블에서 가져온 값
              app.user_is_blacklist === 'Y' ||
              // 또는 불리언 값으로 설정된 경우
              app.isBlacklist === true || 
              userInfo?.isBlacklist === true;
            
            // 상태값 처리 - 숫자로 변환
            let status = app.status;
            if (typeof status === 'string') {
              // 문자열 상태값을 숫자로 변환
              status = statusMapping[status] !== undefined ? statusMapping[status] : 0;
            } else if (status === undefined || status === null) {
              status = 0; // 기본값: applied
            }
            
            return {
              id: appId,
              userId: userId,
              name: formattedName,
              projectId: projectId,
              projectTitle: project.title,
              email: userEmail,
              status: status,
              applied_at: app.applied_at || app.appliedAt || app.createdAt || app.created_at || app.date || new Date().toISOString(),
              age: userAge || '-',
              price: formatPrice(app.price),
              isBlacklist: isBlacklist, // 블랙리스트 상태 추가
              is_read: app.is_read || 'N', // 열람/미열람 상태 추가
              // 원본 데이터도 저장
              raw: app,
              // 사용자 정보 원본
              userInfo: userInfo,
              // 프로젝트 정보 원본
              projectInfo: project
            };
          });
          
          // 제외된 사용자(is_excluded) 필터링
          applicants = showExcludedUsers ? 
            applicants : 
            applicants.filter(app => !isUserExcluded(app.userInfo) && !isUserExcluded(app.raw));
          console.log(`프로젝트 ${project.title}의 지원자 (API):`, applicants.length);
        } else {
          applicants = [];
          console.log(`프로젝트 ${project.title}에 지원자가 없습니다.`);
        }
      } else {
        applicants = [];
        console.log(`프로젝트 ${project.title}의 지원자를 가져오는데 실패했습니다.`);
      }
    } catch (error) {
      console.error('프로젝트 지원자 가져오기 오류:', error);
      applicants = [];
      loadError = `프로젝트 지원자를 불러오는데 실패했습니다: ${error.message}`;
    } finally {
      isLoading = false;
    }
  }
  
  // 지원 상태 변경
  async function updateApplicantStatus(applicant, newStatus) {
    try {
      // 숫자로 변환
      const numericStatus = parseInt(newStatus);
      
      // 이전 상태 저장
      const previousStatus = applicant.status;
      
      // 이전 상태와 같으면 변경하지 않음
      if (previousStatus === numericStatus) {
        return;
      }
      
      // 변경할 상태 레이블 가져오기
      const newStatusLabel = statusOptions.find(option => option.id === numericStatus)?.label || '알 수 없음';
      const prevStatusLabel = statusOptions.find(option => option.id === previousStatus)?.label || '알 수 없음';
      
      // 상태 변경 확인 알림창
      const confirmMessage = `${applicant.name}님의 상태를 '${prevStatusLabel}'에서 '${newStatusLabel}'(으)로 변경하시겠습니까?`;
      if (!confirm(confirmMessage)) {
        // 취소 시 선택 상태 원복
        const select = event.target;
        select.value = previousStatus;
        return;
      }
      
      previousApplicationStatus[applicant.id] = previousStatus;
      
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/api/applications/${applicant.id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          status: numericStatus,
          previousStatus: previousStatus // 이전 상태도 함께 전송
        })
      });
      
      if (!response.ok) {
        throw new Error('지원 상태 변경에 실패했습니다.');
      }
      
      // 성공 시 해당 지원자의 상태 업데이트
      const updatedApplicants = applicants.map(a => 
        a.id === applicant.id ? { ...a, status: numericStatus, previousStatus: previousStatus } : a
      );
      
      applicants = updatedApplicants;
      
      // 상세보기 모달이 열려 있고, 선택된 지원자인 경우 상태 업데이트
      if (isDetailModalOpen && selectedApplicant && selectedApplicant.id === applicant.id) {
        selectedApplicant.status = numericStatus;
        selectedApplicant.previousStatus = previousStatus;
      }
      
      console.log(`지원자 ${applicant.name}의 상태가 ${previousStatus} → ${numericStatus}로 변경되었습니다.`);
      
      // 상태 변경 후 자동으로 이메일 전송 모달 표시 여부 확인
      const statusChangesRequiringEmail = [2, 3, 4, 5, 6, 7, 10, 11]; // proceeding, accepted, rejected, project_canceled, project_delayed, closed, price_difficulty, grade_difficulty
      if (statusChangesRequiringEmail.includes(numericStatus)) {
        generateEmailTemplate(applicant);
      }
      
    } catch (error) {
      console.error('지원 상태 변경 오류:', error);
      alert(`상태 변경 실패: ${error.message}`);
    }
  }
  
  // 지원자 상세 정보 보기
  function viewApplicantDetail(applicant) {
    selectedApplicant = applicant;
    isDetailModalOpen = true;
    // URL에 /detail 추가하여 새로고침 시 상세 모달 자동 열기
    const userId = applicant.userId || selectedApplicantForList?.userId;
    if (userId) {
      window.history.replaceState(
        { page: 'admin-applicants', applicantId: userId, showDetail: true },
        '',
        `#admin-applicants/${userId}/detail`
      );
    }
  }

  // 모달 닫기 함수
  function closeDetailModal() {
    isDetailModalOpen = false;
    selectedApplicant = null;
    // URL에서 /detail 제거
    const userId = selectedApplicantForList?.userId;
    if (userId) {
      window.history.replaceState(
        { page: 'admin-applicants', applicantId: userId },
        '',
        `#admin-applicants/${userId}`
      );
    }
  }
  
  // 지원자 첨부 문서 조회
  async function fetchApplicantFiles(applicant) {
    if (!applicant || !applicant.userId) {
      console.error("지원자의 userId가 없습니다:", applicant);
      return;
    }
    
    try {
      isLoadingFiles = true;
      fileLoadError = null;
      selectedApplicantFiles = [];
      
      const token = localStorage.getItem('token');
      const userId = applicant.userId;
      
      console.log(`지원자 ${applicant.name}의 파일 조회 시작, userId: ${userId}`);
      
      const response = await fetch(`${API_BASE_URL}/api/files/admin/user/${userId}/files`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('파일 목록을 불러오는 데 실패했습니다.');
      }
      
      const data = await response.json();
      
      if (data.success && data.files) {
        selectedApplicantFiles = data.files;
        console.log(`지원자 ${applicant.name}의 파일 ${selectedApplicantFiles.length}개를 가져왔습니다.`);
      } else {
        selectedApplicantFiles = [];
        console.log(`지원자 ${applicant.name}의 파일이 없습니다.`);
      }
      
      isProfileFilesModalOpen = true;
      
    } catch (error) {
      console.error('지원자 파일 조회 오류:', error);
      fileLoadError = error.message;
      selectedApplicantFiles = [];
      isProfileFilesModalOpen = true;
    } finally {
      isLoadingFiles = false;
    }
  }
  
  // 첨부 문서 모달 닫기
  function closeProfileFilesModal() {
    isProfileFilesModalOpen = false;
    selectedApplicantFiles = [];
    fileLoadError = null;
  }
  
  // 파일 다운로드
  async function downloadFile(file, applicant) {
    try {
      const token = localStorage.getItem('token');
      const userId = applicant.userId;
      const fileId = file.id;
      const response = await fetch(`${API_BASE_URL}/api/files/admin/user/${userId}/stream/${fileId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error('파일 다운로드에 실패했습니다.');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.original_name || `download_${fileId}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('파일 다운로드 오류:', error);
      alert(`파일 다운로드 실패: ${error.message}`);
    }
  }
  
  // 프로젝트 수정 페이지로 이동
  function editProject() {
    if (selectedProject) {
      // 프로젝트 수정을 위해 localStorage에 선택된 프로젝트 정보를 저장
      localStorage.setItem('editingProject', JSON.stringify(selectedProject));
      // 프로젝트 등록 페이지로 이동하되, 수정 모드로 전환
      setPage('project-edit');
    }
  }
  
  // 선택된 지원자의 지원 프로젝트 목록 가져오기
  async function selectApplicant(applicant, updateUrl = true) {
    selectedApplicantForList = applicant;
    isLoading = true;
    loadError = null;

    try {
      console.log("지원자 선택:", applicant);

      // 먼저 기존 데이터에서 필터링 시도
      const uniqueKey = applicant.uniqueKey;
      const userId = applicant.userId;

      if (!userId) {
        console.error("지원자의 userId가 없습니다:", applicant);
        loadError = "지원자 정보가 올바르지 않습니다.";
        applicantProjects = [];
        return;
      }

      // URL 업데이트 (브라우저 히스토리에 상태 저장)
      if (updateUrl) {
        window.history.pushState(
          { page: 'admin-applicants', applicantId: userId },
          '',
          `#admin-applicants/${userId}`
        );
      }

      console.log(`지원자 ${applicant.name} (ID: ${userId}) 선택됨, 지원 프로젝트 조회 시작`);
      
      const filteredProjects = applicants.filter(app => {
        // 1. 고유 식별자로 매치 (이메일 또는 ID)
        if (uniqueKey === app.email || uniqueKey === app.id) {
          return true;
        }
        // 2. userId가 있으면 userId로 매치
        else if (userId && app.userId === userId) {
          return true;
        }
        return false;
      });
      
      // 필터링된 프로젝트가 있으면 그대로 사용
      if (filteredProjects.length > 0) {
        applicantProjects = filteredProjects;
        filteredApplicantProjects = filteredProjects;
        console.log(`지원자 ${applicant.name}이(가) 지원한 프로젝트 (로컬 데이터):`, applicantProjects.length);
      } else {
        // 없으면 API에서 다시 가져오기 시도
        console.log("로컬 데이터에서 지원 정보를 찾을 수 없어 API 호출 시도");
        await fetchApplicantProjects(applicant);
      }
    } catch (error) {
      console.error('지원자 프로젝트 처리 오류:', error);
      loadError = `지원자의 프로젝트를 불러오는데 실패했습니다: ${error.message}`;
      applicantProjects = [];
    } finally {
      isLoading = false;
    }
  }

  // 프로젝트별 지원자 수를 계산하는 함수 추가
  function countProjectApplicants(projectId) {
    // 프로젝트 객체에 지원자 수(applicantCount)가 있는 경우 해당 값 사용
    const project = projects.find(p => String(p.id) === String(projectId));
    if (project && project.applicantCount !== undefined) {
      return project.applicantCount;
    }
    
    // 프로젝트의 지원자 수가 없는 경우 기본값 0 반환
    return 0;
  }
  
  // 특정 상태의 지원자 수를 계산하는 함수
  function countProjectApplicantsByStatus(projectId, status) {
    if (!allRawApplicants || allRawApplicants.length === 0) {
      return 0;
    }
    
    const baseData = showExcludedUsers
      ? allRawApplicants
      : allRawApplicants.filter(app => !isUserExcluded(app.userInfo) && !isUserExcluded(app.raw));
    
    return baseData.filter(app => 
      String(app.projectId) === String(projectId) && 
      app.status === parseInt(status)
    ).length;
  }
  
  // 특정 지원자가 해당 상태를 가지는지 확인하는 함수
  function hasApplicantStatus(userId, status) {
    if (!allRawApplicants || allRawApplicants.length === 0) {
      return false;
    }
    
    const baseData = showExcludedUsers
      ? allRawApplicants
      : allRawApplicants.filter(app => !isUserExcluded(app.userInfo) && !isUserExcluded(app.raw));
    
    return baseData.some(app => 
      String(app.userId) === String(userId) && 
      app.status === parseInt(status)
    );
  }
  
  // 프로젝트별 지원자 수 계산
  $: {
    // 프로젝트에 지원자 수 정보 추가
    if (projects && projects.length > 0) {
      projects = projects.map(project => {
        // 프로젝트에 이미 지원자 수 정보가 있으면 그대로 사용
        if (project.applicantCount !== undefined) {
          return project;
        }
        
        // 그렇지 않으면 임시로 0 또는 랜덤한 수 설정 (실제 데이터는 백엔드에서 제공되어야 함)
        const randomCount = Math.floor(Math.random() * 5); // 테스트용 랜덤 수
        return {
          ...project,
          applicantCount: randomCount
        };
      });
    }
  }
  
  // AG Grid 이벤트 핸들러
  function onGridReady(params) {
    gridApi = params.api;
  }

  function onSelectionChanged() {
    const selectedRows = gridApi ? gridApi.getSelectedRows() : [];
    selectedApplicants = selectedRows.map(row => row.id);
  }
  
  // 지원자 프로젝트 그리드 이벤트 핸들러
  function onProjectGridReady(params) {
    gridProjectApi = params.api;
  }

  function onProjectSelectionChanged() {
    const selectedRows = gridProjectApi ? gridProjectApi.getSelectedRows() : [];
    selectedApplicantProjects = selectedRows.map(row => row.id);
  }



  // 다중 선택 관련 변수 추가
  let selectedApplicants = []; // 선택된 지원자 ID 목록
  let selectedApplicantProjects = []; // 선택된 지원자 프로젝트 ID 목록
  let isBulkUpdateModalOpen = false; // 일괄 수정 모달 상태
  let bulkUpdateStatus = 0; // 일괄 수정할 상태

  // 지원자 공고 목록 일괄 상태 변경 관련 변수
  let isProjectBulkUpdateModalOpen = false; // 지원자 공고 일괄 수정 모달 상태
  let projectBulkUpdateStatus = 0; // 지원자 공고 일괄 수정할 상태
  
  // 모든 지원자 선택/해제 함수
  function toggleSelectAllApplicants(event) {
    const isChecked = event.target.checked;
    
    if (isChecked) {
      // 모든 지원자 선택
      selectedApplicants = applicants.map(app => app.id);
    } else {
      // 모든 지원자 선택 해제
      selectedApplicants = [];
    }
  }
  
  // 지원자 선택/해제 함수
  function toggleSelectApplicant(applicantId) {
    const index = selectedApplicants.indexOf(applicantId);
    
    if (index === -1) {
      // 선택되지 않은 경우 추가
      selectedApplicants = [...selectedApplicants, applicantId];
    } else {
      // 이미 선택된 경우 제거
      selectedApplicants = selectedApplicants.filter(id => id !== applicantId);
    }
  }
  
  // 일괄 수정 모달 열기
  function openBulkUpdateModal() {
    if (selectedApplicants.length === 0) {
      alert('먼저 지원자를 선택해주세요.');
      return;
    }
    
    bulkUpdateStatus = 0; // 기본값으로 초기화
    isBulkUpdateModalOpen = true;
  }
  
  // 일괄 수정 모달 닫기
  function closeBulkUpdateModal() {
    isBulkUpdateModalOpen = false;
  }

  // 지원자 공고 일괄 수정 모달 열기
  function openProjectBulkUpdateModal() {
    if (selectedApplicantProjects.length === 0) {
      alert('먼저 공고를 선택해주세요.');
      return;
    }

    projectBulkUpdateStatus = 0; // 기본값으로 초기화
    isProjectBulkUpdateModalOpen = true;
  }

  // 지원자 공고 일괄 수정 모달 닫기
  function closeProjectBulkUpdateModal() {
    isProjectBulkUpdateModalOpen = false;
  }

  // 선택된 지원자 공고들의 상태 일괄 업데이트
  async function bulkUpdateApplicantProjectsStatus() {
    try {
      // 숫자로 변환
      const numericStatus = parseInt(projectBulkUpdateStatus);

      // 새 상태 레이블 가져오기
      const newStatusLabel = statusOptions.find(option => option.id === numericStatus)?.label || '알 수 없음';

      // 상태 변경 확인 알림창
      const confirmMessage = `선택한 ${selectedApplicantProjects.length}건의 공고 상태를 '${newStatusLabel}'(으)로 변경하시겠습니까?`;
      if (!confirm(confirmMessage)) {
        return;
      }

      const token = localStorage.getItem('token');
      let successCount = 0;
      let failCount = 0;

      // 선택된 각 공고에 대해 상태 업데이트 요청
      for (const applicationId of selectedApplicantProjects) {
        try {
          // 해당 공고(지원) 찾기
          const application = filteredApplicantProjects.find(a => a.id === applicationId);
          if (!application) continue;

          // 이전 상태 저장
          const previousStatus = application.status;

          // 상태가 같으면 건너뛰기
          if (previousStatus === numericStatus) continue;

          previousApplicationStatus[applicationId] = previousStatus;

          const response = await fetch(`${API_BASE_URL}/api/applications/${applicationId}/status`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              status: numericStatus,
              previousStatus: previousStatus
            })
          });

          if (response.ok) {
            successCount++;
            // 열람 상태가 미열람이면 자동으로 열람 처리
            if (application.is_read !== 'Y') {
              try {
                await fetch(`${API_BASE_URL}/api/applications/${applicationId}/read-status`, {
                  method: 'PUT',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ is_read: 'Y' })
                });
              } catch (readError) {
                console.error(`지원 ID ${applicationId} 열람 상태 변경 오류:`, readError);
              }
            }
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`지원 ID ${applicationId} 상태 변경 오류:`, error);
          failCount++;
        }
      }

      // 성공 시 지원자 공고 목록 업데이트 (상태 + 열람 처리)
      applicantProjects = applicantProjects.map(a => {
        if (selectedApplicantProjects.includes(a.id)) {
          return { ...a, status: numericStatus, previousStatus: a.status, is_read: 'Y' };
        }
        return a;
      });

      // filteredApplicantProjects도 업데이트 (반응형 업데이트)
      filteredApplicantProjects = filteredApplicantProjects.map(a => {
        if (selectedApplicantProjects.includes(a.id)) {
          return { ...a, status: numericStatus, previousStatus: a.status, is_read: 'Y' };
        }
        return a;
      });

      // 미열람 수 업데이트
      updateUnreadCounts();

      // 모달 닫기 및 선택 초기화
      closeProjectBulkUpdateModal();
      selectedApplicantProjects = [];

      // 결과 알림
      alert(`${successCount}건의 상태가 변경되었습니다.${failCount > 0 ? ` ${failCount}건은 실패했습니다.` : ''}`);

      console.log(`${selectedApplicantProjects.length}건의 지원 상태가 ${numericStatus}로 일괄 변경되었습니다.`);

    } catch (error) {
      console.error('일괄 상태 변경 오류:', error);
      alert(`상태 변경 실패: ${error.message}`);
    }
  }

  // 선택된 지원자들의 상태 일괄 업데이트
  async function bulkUpdateApplicantsStatus() {
    try {
      // 숫자로 변환
      const numericStatus = parseInt(bulkUpdateStatus);
      
      // 새 상태 레이블 가져오기
      const newStatusLabel = statusOptions.find(option => option.id === numericStatus)?.label || '알 수 없음';
      
      // 상태 변경 확인 알림창
      const confirmMessage = `선택한 ${selectedApplicants.length}명의 지원자 상태를 '${newStatusLabel}'(으)로 변경하시겠습니까?`;
      if (!confirm(confirmMessage)) {
        return;
      }
      
      const token = localStorage.getItem('token');
      let successCount = 0;
      let failCount = 0;
      
      // 선택된 각 지원자에 대해 상태 업데이트 요청
      for (const applicantId of selectedApplicants) {
        try {
          // 해당 지원자 찾기
          const applicant = applicants.find(a => a.id === applicantId);
          if (!applicant) continue;
          
          // 이전 상태 저장
          const previousStatus = applicant.status;
          
          // 상태가 같으면 건너뛰기
          if (previousStatus === numericStatus) continue;
          
          previousApplicationStatus[applicantId] = previousStatus;
          
          const response = await fetch(`${API_BASE_URL}/api/applications/${applicantId}/status`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ 
              status: numericStatus,
              previousStatus: previousStatus
            })
          });
          
          if (response.ok) {
            successCount++;
            // 열람 상태가 미열람이면 자동으로 열람 처리
            if (applicant.is_read !== 'Y') {
              try {
                await fetch(`${API_BASE_URL}/api/applications/${applicantId}/read-status`, {
                  method: 'PUT',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ is_read: 'Y' })
                });
              } catch (readError) {
                console.error(`지원자 ID ${applicantId} 열람 상태 변경 오류:`, readError);
              }
            }
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`지원자 ID ${applicantId} 상태 변경 오류:`, error);
          failCount++;
        }
      }

      // 성공 시 지원자 목록 업데이트 (상태 + 열람 처리)
      applicants = applicants.map(a => {
        if (selectedApplicants.includes(a.id)) {
          return { ...a, status: numericStatus, previousStatus: a.status, is_read: 'Y' };
        }
        return a;
      });

      // 미열람 수 업데이트
      updateUnreadCounts();
      
      // 상세보기 모달이 열려 있는 경우 상태 업데이트
      if (isDetailModalOpen && selectedApplicant && selectedApplicants.includes(selectedApplicant.id)) {
        selectedApplicant.status = numericStatus;
        selectedApplicant.previousStatus = selectedApplicant.previousStatus || selectedApplicant.status;
      }
      
      // 모달 닫기 및 선택 초기화
      closeBulkUpdateModal();
      selectedApplicants = [];
      
      // 결과 알림
      alert(`${successCount}명의 상태가 변경되었습니다.${failCount > 0 ? ` ${failCount}명은 실패했습니다.` : ''}`);
      
      console.log(`${selectedApplicants.length}명의 지원자 상태가 ${numericStatus}로 일괄 변경되었습니다.`);
      
    } catch (error) {
      console.error('일괄 상태 변경 오류:', error);
      alert(`상태 변경 실패: ${error.message}`);
    }
  }
  
  // 지원자 삭제 함수 추가
  async function deleteApplicant(applicant, isBlacklist = false) {
    try {
      if (!confirm(`정말로 ${applicant.name} 지원자를 삭제하시겠습니까?`)) {
        return;
      }
      
      const token = localStorage.getItem('token');
      const userId = applicant.userId;
      const applicationId = applicant.id;
      
      if (!userId || !applicationId) {
        throw new Error('지원자 정보가 올바르지 않습니다.');
      }
      
      // 블랙리스트 처리 여부 확인
      let blacklistReason = '';
      if (isBlacklist) {
        blacklistReason = prompt('블랙리스트 등록 사유를 입력하세요:', '');
        if (blacklistReason === null) {
          return; // 취소 버튼 누른 경우
        }
      }

      // 지원 정보 삭제 API 호출
      const deleteResponse = await fetch(`${API_BASE_URL}/api/applications/${applicationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!deleteResponse.ok) {
        throw new Error('지원 정보 삭제 실패');
      }
      
      // 블랙리스트 처리인 경우 사용자 블랙리스트 등록 API 호출
      if (isBlacklist && blacklistReason !== '') {
        const blacklistResponse = await fetch(`${API_BASE_URL}/api/users/${userId}/blacklist`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            is_blacklist: 'Y',
            blacklist_reason: blacklistReason,
            blacklist_date: new Date().toISOString()
          })
        });
        
        if (!blacklistResponse.ok) {
          console.warn('블랙리스트 등록은 실패했으나, 지원 정보는 삭제되었습니다.');
        } else {
          // 블랙리스트 처리가 성공한 경우, 해당 사용자가 포함된 모든 목록에 블랙리스트 상태 업데이트
          applicants = applicants.map(a => {
            if (a.userId === userId) {
              return { ...a, isBlacklist: true };
            }
            return a;
          });
          
          allApplicants = allApplicants.map(a => {
            if (a.userId === userId) {
              return { ...a, isBlacklist: true };
            }
            return a;
          });
          
          filteredAllApplicants = filteredAllApplicants.map(a => {
            if (a.userId === userId) {
              return { ...a, isBlacklist: true };
            }
            return a;
          });
        }
      }
      
      // 지원자 목록에서 삭제
      applicants = applicants.filter(a => a.id !== applicant.id);
      
      // 전체 지원자 목록에서도 삭제
      if (activeTab === 'applicants') {
        allApplicants = allApplicants.filter(a => a.userId !== userId);
        filteredAllApplicants = filteredAllApplicants.filter(a => a.userId !== userId);

        // 선택된 지원자인 경우 선택 해제
        if (selectedApplicantForList && selectedApplicantForList.userId === userId) {
          selectedApplicantForList = null;
          applicantProjects = [];
          // URL을 기본 상태로 복원
          window.history.pushState(
            { page: 'admin-applicants' },
            '',
            '#admin-applicants'
          );
        }
      }
      
      // 상세보기 모달이 열려 있는 경우 닫기
      if (isDetailModalOpen && selectedApplicant && selectedApplicant.id === applicant.id) {
        closeDetailModal();
      }
      
      alert(`${applicant.name} 지원자가 ${isBlacklist ? '블랙리스트로 등록되고 ' : ''}성공적으로 삭제되었습니다.`);
      
    } catch (error) {
      console.error('지원자 삭제 오류:', error);
      alert(`지원자 삭제 실패: ${error.message}`);
    }
  }

  let isBlacklistModalOpen = false;
  let selectedApplicantForBlacklist = null;
  let blacklistReason = '';

  // 블랙리스트 모달 열기
  function openBlacklistModal(applicant) {
    selectedApplicantForBlacklist = applicant;
    blacklistReason = '';
    isBlacklistModalOpen = true;
  }

  // 블랙리스트 모달 닫기
  function closeBlacklistModal() {
    isBlacklistModalOpen = false;
    selectedApplicantForBlacklist = null;
    blacklistReason = '';
  }

  // 블랙리스트 설정
  async function setBlacklist() {
    try {
      if (!selectedApplicantForBlacklist || !blacklistReason.trim()) {
        alert('블랙리스트 사유를 입력해주세요.');
        return;
      }

      const token = localStorage.getItem('token');
      const userId = selectedApplicantForBlacklist.userId;

      if (!userId) {
        throw new Error('사용자 정보가 올바르지 않습니다.');
      }

      const blacklistResponse = await fetch(`${API_BASE_URL}/api/users/${userId}/blacklist`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          is_blacklist: 'Y',
          blacklist_reason: blacklistReason,
          blacklist_date: new Date().toISOString()
        })
      });

      if (!blacklistResponse.ok) {
        throw new Error('블랙리스트 등록 실패');
      }
      
      // API 응답 데이터 가져오기
      const responseData = await blacklistResponse.json();
      console.log("블랙리스트 등록 응답:", responseData);

      // 블랙리스트 처리가 성공한 경우, 해당 사용자가 포함된 모든 목록에 블랙리스트 상태 업데이트
      applicants = applicants.map(a => {
        if (a.userId === userId) {
          return { 
            ...a, 
            isBlacklist: true,
            // 사용자 정보 업데이트 (is_blacklist, blacklist_reason, blacklist_date)
            userInfo: a.userInfo ? {
              ...a.userInfo,
              is_blacklist: 'Y',
              blacklist_reason: blacklistReason,
              blacklist_date: new Date().toISOString()
            } : null
          };
        }
        return a;
      });

      allApplicants = allApplicants.map(a => {
        if (a.userId === userId) {
          return { 
            ...a, 
            isBlacklist: true,
            // 사용자 정보 업데이트
            userInfo: a.userInfo ? {
              ...a.userInfo,
              is_blacklist: 'Y',
              blacklist_reason: blacklistReason,
              blacklist_date: new Date().toISOString()
            } : null
          };
        }
        return a;
      });

      filteredAllApplicants = filteredAllApplicants.map(a => {
        if (a.userId === userId) {
          return { 
            ...a, 
            isBlacklist: true,
            // 사용자 정보 업데이트
            userInfo: a.userInfo ? {
              ...a.userInfo,
              is_blacklist: 'Y',
              blacklist_reason: blacklistReason,
              blacklist_date: new Date().toISOString()
            } : null
          };
        }
        return a;
      });
      
      // 전역 데이터도 업데이트
      if (typeof window !== 'undefined' && window.globalApplicantsData) {
        window.globalApplicantsData = window.globalApplicantsData.map(a => {
          if (a.userId === userId) {
            return {
              ...a,
              isBlacklist: true,
              userInfo: a.userInfo ? {
                ...a.userInfo,
                is_blacklist: 'Y',
                blacklist_reason: blacklistReason,
                blacklist_date: new Date().toISOString()
              } : null
            };
          }
          return a;
        });
      }
      
      // 선택된 지원자가 있는 경우 해당 데이터도 업데이트
      if (selectedApplicant && selectedApplicant.userId === userId) {
        selectedApplicant = {
          ...selectedApplicant,
          isBlacklist: true,
          userInfo: selectedApplicant.userInfo ? {
            ...selectedApplicant.userInfo,
            is_blacklist: 'Y',
            blacklist_reason: blacklistReason,
            blacklist_date: new Date().toISOString()
          } : null
        };
      }

      alert(`${selectedApplicantForBlacklist.name} 지원자가 블랙리스트로 등록되었습니다.`);
      closeBlacklistModal();

    } catch (error) {
      console.error('블랙리스트 등록 오류:', error);
      alert(`블랙리스트 등록 실패: ${error.message}`);
    }
  }

  // 블랙리스트 해제
  async function removeBlacklist(applicant) {
    try {
      if (!confirm(`${applicant.name} 지원자의 블랙리스트를 해제하시겠습니까?`)) {
        return;
      }

      const token = localStorage.getItem('token');
      const userId = applicant.userId;

      if (!userId) {
        throw new Error('사용자 정보가 올바르지 않습니다.');
      }

      const blacklistResponse = await fetch(`${API_BASE_URL}/api/users/${userId}/blacklist`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          is_blacklist: 'N',
          blacklist_reason: '',
          blacklist_date: null
        })
      });

      if (!blacklistResponse.ok) {
        throw new Error('블랙리스트 해제 실패');
      }
      
      // API 응답 데이터 가져오기
      const responseData = await blacklistResponse.json();
      console.log("블랙리스트 해제 응답:", responseData);

      // 블랙리스트 해제가 성공한 경우, 해당 사용자가 포함된 모든 목록에 블랙리스트 상태 업데이트
      applicants = applicants.map(a => {
        if (a.userId === userId) {
          return { 
            ...a, 
            isBlacklist: false,
            // 사용자 정보 업데이트
            userInfo: a.userInfo ? {
              ...a.userInfo,
              is_blacklist: 'N',
              blacklist_reason: '',
              blacklist_date: null
            } : null
          };
        }
        return a;
      });

      allApplicants = allApplicants.map(a => {
        if (a.userId === userId) {
          return { 
            ...a, 
            isBlacklist: false,
            // 사용자 정보 업데이트
            userInfo: a.userInfo ? {
              ...a.userInfo,
              is_blacklist: 'N',
              blacklist_reason: '',
              blacklist_date: null
            } : null
          };
        }
        return a;
      });

      filteredAllApplicants = filteredAllApplicants.map(a => {
        if (a.userId === userId) {
          return { 
            ...a, 
            isBlacklist: false,
            // 사용자 정보 업데이트
            userInfo: a.userInfo ? {
              ...a.userInfo,
              is_blacklist: 'N',
              blacklist_reason: '',
              blacklist_date: null
            } : null
          };
        }
        return a;
      });
      
      // 전역 데이터도 업데이트
      if (typeof window !== 'undefined' && window.globalApplicantsData) {
        window.globalApplicantsData = window.globalApplicantsData.map(a => {
          if (a.userId === userId) {
            return {
              ...a,
              isBlacklist: false,
              userInfo: a.userInfo ? {
                ...a.userInfo,
                is_blacklist: 'N',
                blacklist_reason: '',
                blacklist_date: null
              } : null
            };
          }
          return a;
        });
      }
      
      // 선택된 지원자가 있는 경우 해당 데이터도 업데이트
      if (selectedApplicant && selectedApplicant.userId === userId) {
        selectedApplicant = {
          ...selectedApplicant,
          isBlacklist: false,
          userInfo: selectedApplicant.userInfo ? {
            ...selectedApplicant.userInfo,
            is_blacklist: 'N',
            blacklist_reason: '',
            blacklist_date: null
          } : null
        };
      }

      alert(`${applicant.name} 지원자의 블랙리스트가 해제되었습니다.`);

    } catch (error) {
      console.error('블랙리스트 해제 오류:', error);
      alert(`블랙리스트 해제 실패: ${error.message}`);
    }
  }

  let userFilesCache = {}; // userId -> boolean 첨부 존재 여부

  // 지원 목록/프로젝트 목록에 대한 첨부파일 존재 여부 초기화
  async function updateHasFilesForList(list) {
    if (!list || list.length === 0) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    // 중복 호출 방지
    const uniqueUsers = [...new Set(list.map(item => item.userId).filter(Boolean))];

    const promises = uniqueUsers.map(async (uid) => {
      if (userFilesCache[uid] !== undefined) return; // 이미 확인 됨
      try {
        const res = await fetch(`${API_BASE_URL}/api/files/admin/user/${uid}/files?limit=1`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (res.ok) {
          const data = await res.json();
          let has = false;
          if (data && typeof data === 'object') {
            if (Array.isArray(data.files)) has = data.files.length > 0;
            else if (Array.isArray(data)) has = data.length > 0;
          }
          userFilesCache[uid] = has;
        } else {
          userFilesCache[uid] = false;
        }
      } catch (e) {
        userFilesCache[uid] = false;
      }
    });
    await Promise.all(promises);

    // 리스트의 hasFiles 속성 업데이트
    let changed = false;
    list.forEach(item => {
      if (item.userId && userFilesCache[item.userId] !== undefined && item.hasFiles !== userFilesCache[item.userId]) {
        item.hasFiles = userFilesCache[item.userId];
        changed = true;
      }
    });
    if (changed) {
      // 배열 재할당로 reactivity 트리거
      if (list === applicants) {
        applicants = [...applicants];
      } else if (list === applicantProjects) {
        applicantProjects = [...applicantProjects];
      }
    }
  }

  // reactive: applicants list changes
  $: if (applicants && applicants.length > 0) {
    updateHasFilesForList(applicants);
  }

  // reactive: applicantProjects list changes
  $: if (applicantProjects && applicantProjects.length > 0) {
    updateHasFilesForList(applicantProjects);
  }

  // 페이지 변경 이벤트 핸들러
  function handlePageChange(event) {
    setPage(event.detail.page);
  }

  // 로그아웃 이벤트 핸들러
  function handleLogout() {
    // 로컬 스토리지에서 사용자 정보 및 토큰 삭제
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    user = null;
    isAdmin = false;
    isCompany = false;
    // 홈페이지로 이동
    setPage('home');
  }

  onMount(() => {
    // 모바일 감지 초기화
    isMobile = checkMobileDevice();
    window.addEventListener('resize', handleResize);
    
    window.addEventListener('pagechange', handlePageChange);
    window.addEventListener('logout', handleLogout);
  });

  onDestroy(() => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('pagechange', handlePageChange);
    window.removeEventListener('logout', handleLogout);
  });

  // ======= 모바일 체크박스 및 버튼 이벤트 처리 함수들 =======
  function handleMobileCheckboxChange(event) {
    const checkbox = event.target;
    const applicantId = checkbox.dataset.applicantId;
    const isChecked = checkbox.checked;
    
    if (isChecked) {
      // 체크된 경우: 해당 지원자 ID를 선택 목록에 추가
      if (!selectedApplicants.includes(applicantId)) {
        selectedApplicants = [...selectedApplicants, applicantId];
      }
    } else {
      // 체크 해제된 경우: 선택 목록에서 제거
      selectedApplicants = selectedApplicants.filter(id => id !== applicantId);
    }
  }

  function handleMobileStatusChange(event) {
    const select = event.target;
    const applicantId = select.dataset.applicantId;
    const newStatus = parseInt(select.value);
    
    const applicant = applicants.find(a => a.id === applicantId);
    if (applicant) {
      updateApplicantStatus(applicant, newStatus);
    }
  }

  function handleMobileProjectStatusChange(event) {
    const select = event.target;
    const applicationId = select.dataset.projectApplicationId;
    const newStatus = parseInt(select.value);
    
    const applicant = applicantProjects.find(a => a.id === applicationId);
    if (applicant) {
      updateApplicantStatus(applicant, newStatus);
    }
  }

  function handleMobileDocsClick(event) {
    const button = event.target;
    const applicantId = button.dataset.applicantId;
    
    const applicant = applicants.find(a => a.id === applicantId);
    if (applicant) {
      fetchApplicantFiles(applicant);
    }
  }

  function handleMobileProjectDocsClick(event) {
    const button = event.target;
    const applicationId = button.dataset.projectApplicationId;
    
    const applicant = applicantProjects.find(a => a.id === applicationId);
    if (applicant) {
      fetchApplicantFiles(applicant);
    }
  }

  function handleMobileDetailClick(event) {
    const button = event.target;
    const applicantId = button.dataset.applicantId;
    
    const applicant = applicants.find(a => a.id === applicantId);
    if (applicant) {
      viewApplicantDetail(applicant);
    }
  }

  function handleMobileProjectDetailClick(event) {
    const button = event.target;
    const applicationId = button.dataset.projectApplicationId;
    
    const applicant = applicantProjects.find(a => a.id === applicationId);
    if (applicant) {
      viewApplicantDetail(applicant);
    }
  }

  // 모바일 이벤트 리스너 설정
  function setupMobileEventListeners() {
    if (!isMobile) return;
    
    setTimeout(() => {
      // 체크박스 이벤트
      document.querySelectorAll('.mobile-checkbox').forEach(checkbox => {
        checkbox.addEventListener('change', handleMobileCheckboxChange);
      });
      
      // 상태 변경 이벤트
      document.querySelectorAll('.mobile-status-select').forEach(select => {
        if (select.dataset.applicantId) {
          select.addEventListener('change', handleMobileStatusChange);
        } else if (select.dataset.projectApplicationId) {
          select.addEventListener('change', handleMobileProjectStatusChange);
        }
      });
      
      // 첨부 문서 버튼 이벤트
      document.querySelectorAll('.js-mobile-docs-btn').forEach(button => {
        button.addEventListener('click', handleMobileDocsClick);
      });
      
      document.querySelectorAll('.js-mobile-project-docs-btn').forEach(button => {
        button.addEventListener('click', handleMobileProjectDocsClick);
      });
      
      // 상세 버튼 이벤트
      document.querySelectorAll('.js-mobile-detail-btn').forEach(button => {
        button.addEventListener('click', handleMobileDetailClick);
      });
      
      document.querySelectorAll('.js-mobile-project-detail-btn').forEach(button => {
        button.addEventListener('click', handleMobileProjectDetailClick);
      });
    }, 100);
  }

  // 그리드 데이터 변경 시 모바일 이벤트 리스너 재설정
  $: if (isMobile && (applicants || applicantProjects)) {
    setupMobileEventListeners();
  }

  // 제외된 회원 보기 또는 전역 데이터 변경 시 allApplicants 목록 업데이트
  $: if (false) {
    if (typeof window !== 'undefined' && window.globalApplicantsData) {
      const sourceData = window.globalApplicantsData;
      const filteredData = showExcludedUsers
        ? sourceData.filter(app => !isUserExcluded(app.userInfo) && !isUserExcluded(app.raw))
        : sourceData;
      allApplicants = Object.values(filteredData);
    }
  }
  
  // 검색어 또는 allApplicants 변경 시 최종 목록 필터링
  $: if (false) {
    if (applicantSearchTerm.trim() === "") {
      filteredAllApplicants = allApplicants;
    } else {
      const searchLower = applicantSearchTerm.toLowerCase();
      filteredAllApplicants = allApplicants.filter(applicant => 
        applicant.name?.toLowerCase().includes(searchLower) || 
        applicant.email?.toLowerCase().includes(searchLower)
      );
    }
  }

  // 프로젝트 상세 모달 상태 및 함수 추가
  let isProjectDetailModalOpen = false;
  let projectDetail = null;
  let isLoadingProjectDetail = false;
  let projectDetailError = null;

  async function openProjectDetailModal(projectId) {
    isLoadingProjectDetail = true;
    projectDetailError = null;
    projectDetail = null;
    isProjectDetailModalOpen = true;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error('프로젝트 정보를 불러오지 못했습니다.');
      }
      const data = await response.json();
      projectDetail = data.project || null;
    } catch (err) {
      projectDetailError = err.message;
    } finally {
      isLoadingProjectDetail = false;
    }
  }
  function closeProjectDetailModal() {
    isProjectDetailModalOpen = false;
    projectDetail = null;
    projectDetailError = null;
  }
</script>

<div class="admin-applicants-container">
  <Navigation {user} currentPage="admin-applicants" />

  <div class="page-header">
    <h2 class="page-title">지원자 관리</h2>
    <div class="page-options">
      <div class="filter-options">
        <div class="checkbox-group">
          <label class="modern-checkbox">
            <input 
              type="checkbox" 
              bind:checked={showExcludedUsers}
              id="showExcludedUsers"
            />
            <span class="checkbox-custom"></span>
            <span class="checkbox-label">제외된 회원 포함</span>
          </label>
          
          <label class="modern-checkbox">
            <input 
              type="checkbox" 
              bind:checked={excludeExpiredProjects}
              id="excludeExpiredProjects"
            />
            <span class="checkbox-custom"></span>
            <span class="checkbox-label">마감 제외</span>
          </label>
        </div>
      </div>
    </div>
  </div>
  
  {#if isLoading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <div class="loading-text">데이터를 불러오는 중...</div>
    </div>
  {:else if loadError}
    <div class="error-container">
      <div class="error-icon">!</div>
      <div class="error-text">{loadError}</div>
      <button class="retry-btn" on:click={fetchProjects}>다시 시도</button>
    </div>
  {:else}
    <div class="admin-content-grid">
      <!-- 탭 메뉴가 있는 좌측 섹션 -->
      <div class="admin-left-section">
        <div class="admin-tabs">
          <button 
            class="admin-tab-btn" 
            class:active={activeTab === 'projects'} 
            on:click={() => changeTab('projects')}
          >
            공고
          </button>
          <button 
            class="admin-tab-btn" 
            class:active={activeTab === 'applicants'} 
            on:click={() => changeTab('applicants')}
          >
            지원자
          </button>
        </div>
        
        {#if activeTab === 'projects'}
          <!-- 프로젝트 목록 섹션 -->
          <div class="admin-project-list-section">
            <div class="admin-search-container">
              <input 
                type="text" 
                placeholder="공고명, 회사명으로 검색" 
                bind:value={projectSearchTerm} 
                class="admin-search-input"
              />
              {#if projectSearchTerm}
                <button class="admin-clear-search-btn" on:click={clearSearch}>✕</button>
              {/if}
            </div>
            
            <!-- 상태 필터 -->
            <div class="admin-filter-container">
              <label for="project-status-filter">상태:</label>
              <select id="project-status-filter" bind:value={selectedStatusFilter} class="admin-status-filter">
                <option value="all">전체</option>
                {#each statusOptions as option}
                  <option value={option.id}>{option.label}</option>
                {/each}
              </select>
            </div>
            
            <h3>공고 목록 {filteredProjects.length > 0 ? `(${filteredProjects.length}개)` : ''}</h3>
            {#if projects.length === 0}
              <div class="empty-state text-center">등록된 공고가 없습니다.</div>
            {:else if filteredProjects.length === 0}
              <div class="empty-state text-center">검색 결과가 없습니다.</div>
            {:else}
              <ul class="admin-project-list">
                {#each filteredProjects as project (project.id)}
                  <li 
                    class="admin-project-item" 
                    class:selected={selectedProject && selectedProject.id === project.id}
                    on:click={() => selectProject(project)}
                    on:keydown={(e) => e.key === 'Enter' && selectProject(project)}
                  >
                    <div class="admin-project-name">{project.title}</div>
                    <div class="admin-project-info">
                      <span class="admin-project-company">{project.company || '비공개'}</span>
                      <span class="admin-project-count">지원 {countProjectApplicants(project.id)}건</span>
                    </div>
                  </li>
                {/each}
              </ul>
            {/if}
          </div>
        {:else}
          <!-- 지원자 목록 섹션 -->
          <div class="admin-project-list-section">
            <div class="admin-search-container">
              <input 
                type="text" 
                placeholder="이름, 이메일로 검색" 
                bind:value={applicantSearchTerm} 
                class="admin-search-input"
              />
              {#if applicantSearchTerm}
                <button class="admin-clear-search-btn" on:click={clearSearch}>✕</button>
              {/if}
            </div>
            
            <!-- 상태 필터 -->
            <div class="admin-filter-container">
              <label for="applicant-status-filter">상태:</label>
              <select id="applicant-status-filter" bind:value={selectedStatusFilter} class="admin-status-filter">
                <option value="all">전체</option>
                {#each statusOptions as option}
                  <option value={option.id}>{option.label}</option>
                {/each}
              </select>
            </div>
            
            <h3>지원자 목록 {filteredAllApplicants.length > 0 ? `(${filteredAllApplicants.length}명)` : ''}</h3>
            {#if allApplicants.length === 0}
              <div class="empty-state text-center">등록된 지원자가 없습니다.</div>
            {:else if filteredAllApplicants.length === 0}
              <div class="empty-state text-center">검색 결과가 없습니다.</div>
            {:else}
              <ul class="admin-project-list">
                {#each filteredAllApplicants as applicant (applicant.id)}
                  <li 
                    class="admin-project-item" 
                    class:selected={selectedApplicantForList && selectedApplicantForList.id === applicant.id}
                    on:click={() => selectApplicant(applicant)}
                    on:keydown={(e) => e.key === 'Enter' && selectApplicant(applicant)}
                  >
                    <div class="admin-project-name">
                      <span class="applicant-name-text">
                        {applicant.name}
                        {#if applicant.age && applicant.age !== '-'}
                          <span class="applicant-age"> ({applicant.age}세)</span>
                        {/if}
                      </span>
                      {#if applicant.isBlacklist}
                        <span class="blacklist-tag">Blacklist</span>
                      {/if}
                    </div>
                    <div class="admin-project-info">
                      <span class="admin-project-company">{applicant.email}</span>
                      <span class="admin-project-count">미열람 {applicant.unreadCount || 0} / 지원 {applicant.projectCount}건</span>
                    </div>
                  </li>
                {/each}
              </ul>
            {/if}
          </div>
        {/if}
      </div>
      
      <!-- 우측 섹션 - 선택된 프로젝트의 지원자 또는 선택된 지원자의 프로젝트 -->
      <div class="applicants-section">
        {#if activeTab === 'projects' && selectedProject}
          <div class="applicants-header">
            <h3>{selectedProject.title} - 지원자 목록</h3>
            <div class="header-actions">
              <span class="applicant-count">총 {applicants.length}명</span>
              <button class="view-project-btn" on:click={() => openProjectDetailModal(selectedProject.id)}>공고 보기</button>
              <button class="edit-project-btn" on:click={editProject}>공고 수정</button>
            </div>
          </div>
          
          {#if applicants.length === 0}
            <div class="empty-message">지원자가 없습니다.</div>
          {:else}
            <!-- 다중 선택 컨트롤 추가 -->
            <div class="bulk-actions">
              {#if selectedApplicants.length > 0}
                <button class="bulk-update-btn" on:click={openBulkUpdateModal}>
                  선택한 {selectedApplicants.length}명 상태 변경
                </button>
              {/if}
            </div>
            
            <!-- AG Grid 컨테이너 -->
            <div class="ag-theme-alpine applicants-grid" class:mobile-grid={isMobile} style="height: {isMobile ? 'auto' : '500px'}; width: 100%;">
              <AgGridSvelte
                rowData={applicants}
                columnDefs={applicantGridColumnDefs}
                {...applicantGridOptions}
                defaultColDef={{
                  sortable: true,
                  filter: true,
                  resizable: true,
                  flex: 1,
                  minWidth: 100
                }}
                pagination={true}
                paginationPageSize={paginationPageSize}
                quickFilterText={quickFilterText}
                onGridReady={onGridReady}
                onSelectionChanged={onSelectionChanged}
                animateRows={true}
                enableRangeSelection={true}
                suppressMenuHide={true}
              />
            </div>
          {/if}
        {:else if activeTab === 'applicants' && selectedApplicantForList}
          <div class="applicants-header">
            <h3>{selectedApplicantForList.name} - 지원 공고 목록</h3>
            <div class="header-actions">
              <select 
                class="status-filter-select"
                bind:value={selectedStatusFilter}
                on:change={() => filterApplicantProjectsByStatus()}
              >
                <option value="all">전체 상태</option>
                {#each statusOptions as status}
                  <option value={status.id}>{status.label}</option>
                {/each}
              </select>
              <span class="applicant-count">총 {filteredApplicantProjects.length}건</span>
            </div>
          </div>
          
          {#if filteredApplicantProjects.length === 0}
            <div class="empty-message">지원한 공고가 없습니다.</div>
          {:else}
            <!-- 일괄 작업 버튼 -->
            <div class="bulk-actions2">
              {#if selectedApplicantProjects.length > 0}
                <button class="bulk-status-btn" on:click={openProjectBulkUpdateModal}>
                  선택한 {selectedApplicantProjects.length}건 상태 변경
                </button>
                <button class="bulk-unread-btn" on:click={() => markAsUnread(selectedApplicantProjects)}>
                  선택한 {selectedApplicantProjects.length}건 미열람으로 복구
                </button>
              {/if}
            </div>
            
            <!-- AG Grid 컨테이너 (지원자 프로젝트 목록) -->
            <div class="ag-theme-alpine applicants-grid" class:mobile-grid={isMobile} style="height: {isMobile ? 'auto' : '500px'}; width: 100%;">
              <AgGridSvelte
                rowData={filteredApplicantProjects}
                columnDefs={applicantProjectGridColumnDefs}
                {...applicantProjectGridOptions}
                defaultColDef={{
                  sortable: true,
                  filter: true,
                  resizable: true,
                  flex: 1,
                  minWidth: 100
                }}
                pagination={true}
                paginationPageSize={paginationPageSize}
                onGridReady={onProjectGridReady}
                onSelectionChanged={onProjectSelectionChanged}
                animateRows={true}
                enableRangeSelection={true}
                suppressMenuHide={true}
              />
            </div>
          {/if}
        {:else}
          <div class="applicants-header">
            <h3>
              {#if activeTab === 'projects'}
                공고를 선택하세요
              {:else}
                지원자를 선택하세요
              {/if}
            </h3>
          </div>
          <div class="empty-message center">
            {#if activeTab === 'projects'}
              좌측에서 공고를 선택하면 지원자 목록이 표시됩니다.
            {:else}
              좌측에서 지원자를 선택하면 지원한 공고 목록이 표시됩니다.
            {/if}
          </div>
        {/if}
      </div>
    </div>
  {/if}
  
</div>

{#if isDetailModalOpen && selectedApplicant}
  <div class="modal-overlay" on:click={closeDetailModal} on:keydown={(e) => e.key === 'Escape' && closeDetailModal()}>
    <div class="modal-container" on:click|stopPropagation on:keydown={(e) => e.key === 'Escape' && closeDetailModal()}>
      <div class="modal-header">
        <h3>지원자 상세 정보</h3>
        <button class="close-modal-btn" on:click={closeDetailModal}>✕</button>
      </div>
      
      <div class="modal-content">
        <div class="detail-section">
          <h4>기본 정보</h4>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">이름</span>
              <span class="detail-value">{selectedApplicant.name || '이름 없음'}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">이메일</span>
              <span class="detail-value">{selectedApplicant.email || '-'}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">연락처</span>
              <span class="detail-value">
                {formatPhoneNumber(selectedApplicant.userInfo?.phone || selectedApplicant.userInfo?.mobile || selectedApplicant.raw?.phone || selectedApplicant.raw?.mobile || selectedApplicant.raw?.user?.phone || selectedApplicant.raw?.user?.mobile)}
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">성별</span>
              <span class="detail-value">
                {(() => {
                  const gender = selectedApplicant.userInfo?.gender || selectedApplicant.raw?.gender || selectedApplicant.raw?.user?.gender;
                  if (gender === 'M') return '남자';
                  if (gender === 'F') return '여자';
                  return '-';
                })()}
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">나이</span>
              <span class="detail-value">
                {(() => {
                  const birthDateStr = selectedApplicant.userInfo?.birth_date || 
                                       selectedApplicant.userInfo?.birthDate || 
                                       selectedApplicant.raw?.birth_date || 
                                       selectedApplicant.raw?.birthDate || 
                                       selectedApplicant.raw?.user?.birth_date;
                  
                  let agePart = '';
                  const ageFromProp = selectedApplicant.age;

                  if (ageFromProp && ageFromProp !== '-') {
                    agePart = `${ageFromProp}세`;
                  } else if (birthDateStr) {
                    const calculatedAge = calculateAge(birthDateStr);
                    if (calculatedAge !== '') {
                      agePart = `${calculatedAge}세`;
                    }
                  }

                  let birthDatePart = '';
                  if (birthDateStr) {
                    let birthDate;
                    const cleanedDateStr = String(birthDateStr).trim();
                    
                    if (/^\d{4}$/.test(cleanedDateStr)) {
                      const year = parseInt(cleanedDateStr, 10);
                      if (year > 1900 && year <= new Date().getFullYear()) {
                        birthDate = new Date(year, 0, 1);
                      }
                    } else {
                      birthDate = new Date(cleanedDateStr);
                    }

                    if (birthDate && !isNaN(birthDate.getTime())) {
                      const year = birthDate.getFullYear();
                      const month = birthDate.getMonth() + 1;
                      const day = birthDate.getDate();
                      birthDatePart = `${year}년 ${month}월 ${day}일`;
                    }
                  }

                  if (agePart && birthDatePart) {
                    return `${agePart} (${birthDatePart})`;
                  } else if (agePart) {
                    return agePart;
                  } else if (birthDatePart) {
                    return birthDatePart;
                  }
                  
                  return '-';
                })()}
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">최종학력</span>
              <span class="detail-value">
                {#if selectedApplicant.userInfo?.education_level === 'none' || selectedApplicant.userInfo?.educationLevel === 'none'}
                  공개 안 함
                {:else if selectedApplicant.userInfo?.education_level === 'highschool' || selectedApplicant.userInfo?.educationLevel === 'highschool'}
                  고등학교
                {:else if selectedApplicant.userInfo?.education_level === 'associate' || selectedApplicant.userInfo?.educationLevel === 'associate'}
                  전문학사
                {:else if selectedApplicant.userInfo?.education_level === 'bachelor' || selectedApplicant.userInfo?.educationLevel === 'bachelor'}
                  학사
                {:else if selectedApplicant.userInfo?.education_level === 'master' || selectedApplicant.userInfo?.educationLevel === 'master'}
                  석사
                {:else if selectedApplicant.userInfo?.education_level === 'doctorate' || selectedApplicant.userInfo?.educationLevel === 'doctorate'}
                  박사
                {:else}
                  {selectedApplicant.userInfo?.education_level || selectedApplicant.userInfo?.educationLevel || '-'}
                {/if}
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">경력 기간</span>
              <span class="detail-value">
                {formatCareer(
                  selectedApplicant.userInfo?.career_year ?? selectedApplicant.userInfo?.careerYear,
                  selectedApplicant.userInfo?.career_month ?? selectedApplicant.userInfo?.careerMonth
                )}
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">지원일자</span>
              <span class="detail-value">
                {#if selectedApplicant.applied_at}
                  {new Date(selectedApplicant.applied_at).toLocaleString()}
                {:else}
                  -
                {/if}
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">면접가능일</span>
              <span class="detail-value">
                {#if selectedApplicant.available_date && selectedApplicant.available_date !== '-'}
                  {selectedApplicant.available_date}
                {:else}
                  -
                {/if}
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">출근가능일</span>
              <span class="detail-value">
                {#if selectedApplicant.start_work_date && selectedApplicant.start_work_date !== '-'}
                  {selectedApplicant.start_work_date}
                {:else}
                  -
                {/if}
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">최종 수정일</span>
              <span class="detail-value">
                {#if selectedApplicant.userInfo?.updated_at}
                  {new Date(selectedApplicant.userInfo.updated_at).toLocaleString()}
                {:else if selectedApplicant.raw?.updated_at}
                  {new Date(selectedApplicant.raw.updated_at).toLocaleString()}
                {:else}
                  -
                {/if}
              </span>
            </div>
          </div>
        </div>
        
        <div class="detail-section">
          <h4>지원 정보</h4>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">프로젝트</span>
              <span class="detail-value">{selectedApplicant.projectTitle || '프로젝트명 없음'}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">단가</span>
              <span class="detail-value">{selectedApplicant.price || '-'}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">상태</span>
              <span class="detail-value">
                <select 
                  value={selectedApplicant.status}
                  on:change={(e) => {
                    updateApplicantStatus(selectedApplicant, e.target.value);
                    // updateApplicantStatus 함수 내에서 확인 작업이 수행되므로
                    // 취소된 경우 원래 상태로 돌아갈 수 있도록 상태 업데이트는 함수 내에서 처리
                  }}
                >
                  {#each statusOptions as option}
                    <option value={option.id}>{option.label}</option>
                  {/each}
                </select>
                {#if selectedApplicant.previousStatus}
                  <span class="previous-status">
                    이전 상태: {statusOptions.find(s => s.id === selectedApplicant.previousStatus)?.label || selectedApplicant.previousStatus}
                  </span>
                {/if}
              </span>
            </div>
          </div>
        </div>
        
        <!-- 지원 메시지 표시 - 여러 필드 확인 -->
        {#if selectedApplicant.raw && (selectedApplicant.raw.message || selectedApplicant.raw.applicant_message)}
          <div class="detail-section">
            <h4>지원 메시지</h4>
            <div class="message-box">
              {selectedApplicant.raw.message || selectedApplicant.raw.applicant_message || '지원 메시지가 없습니다.'}
            </div>
          </div>
        {:else if selectedApplicant.message}
          <div class="detail-section">
            <h4>지원 메시지</h4>
            <div class="message-box">
              {selectedApplicant.message}
            </div>
          </div>
        {:else if selectedApplicant.raw}
          <div class="detail-section">
            <h4>지원 메시지</h4>
            <div class="message-box">
              지원 메시지가 없습니다.
            </div>
          </div>
        {/if}
        
        <div class="detail-section">
          <h4>원본 데이터</h4>
          <div class="raw-data-toggle">
            <button class="toggle-btn" on:click={(event) => {
              const rawDataElement = document.getElementById('rawData');
              const isHidden = rawDataElement.classList.contains('hidden');
              rawDataElement.classList.toggle('hidden');
              // 버튼 텍스트 변경
              event.target.textContent = `원본 데이터 ${isHidden ? '숨기기' : '보기'}`;
            }}>
              원본 데이터 보기
            </button>
          </div>
          <pre id="rawData" class="raw-data hidden">{JSON.stringify(selectedApplicant.raw, null, 2)}</pre>
        </div>
      </div>
      
      <div class="modal-footer">
        <div class="modal-actions-left">
          {#if selectedApplicant.isBlacklist}
            <button class="modal-btn unblacklist" on:click={() => removeBlacklist(selectedApplicant)}>
              블랙리스트 해제
            </button>
          {:else}
            <button class="modal-btn blacklist" on:click={() => openBlacklistModal(selectedApplicant)}>
              블랙리스트
            </button>
          {/if}
        </div>
        <div class="modal-actions-right">
          <button class="modal-btn cancel" on:click={closeDetailModal}>닫기</button>
          <button 
            class="modal-btn profile-docs" 
            on:click={() => fetchApplicantFiles(selectedApplicant)}
            disabled={!selectedApplicant.userId}
          >
            📄 첨부 문서
          </button>
          <button
            class="modal-btn interview"
            on:click={openInterviewModal}
          >
            인터뷰 등록
          </button>
          <button
            class="modal-btn primary"
            on:click={() => generateEmailTemplate(selectedApplicant)}
          >
            이메일 전송
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<!-- 인터뷰 등록 모달 -->
{#if isInterviewModalOpen}
  <div class="modal-overlay" on:click={() => (isInterviewModalOpen = false)} on:keydown={(e) => e.key === 'Escape' && (isInterviewModalOpen = false)}>
    <div class="modal-container interview-modal" on:click|stopPropagation on:keydown={(e) => e.key === 'Escape' && (isInterviewModalOpen = false)}>
      <div class="modal-header">
        <h3>인터뷰 등록</h3>
        <button class="close-modal-btn" on:click={() => (isInterviewModalOpen = false)}>✕</button>
      </div>
      <div class="modal-content">
        <div class="interview-form-group">
          <label for="iv-status">진행상황</label>
          <select id="iv-status" bind:value={interviewForm.status}>
            {#each interviewStatusList as st}
              <option value={st.value}>{st.label}</option>
            {/each}
          </select>
        </div>
        <div class="interview-form-group">
          <label for="iv-projectName">프로젝트명 <span class="iv-required">*</span></label>
          <input id="iv-projectName" type="text" bind:value={interviewForm.projectName} />
        </div>
        <div class="interview-form-group">
          <label for="iv-candidateName">대상자 <span class="iv-required">*</span></label>
          <input id="iv-candidateName" type="text" bind:value={interviewForm.candidateName} />
        </div>
        <div class="interview-form-group">
          <label for="iv-interviewDate">면접 일시</label>
          <input id="iv-interviewDate" type="datetime-local" bind:value={interviewForm.interviewDate} />
        </div>
        <div class="interview-form-group">
          <label for="iv-location">장소</label>
          <input id="iv-location" type="text" bind:value={interviewForm.location} placeholder="예: 서울시 서초구 사평대로 60 덕명빌딩 4F" />
        </div>
        <div class="interview-form-group">
          <label for="iv-contactChannel">채널 (담당자/연락처)</label>
          <input id="iv-contactChannel" type="text" bind:value={interviewForm.contactChannel} placeholder="예: 유충현 수석(010-6847-3780)" />
        </div>
        <div class="interview-form-group">
          <label for="iv-notes">참고사항</label>
          <textarea id="iv-notes" bind:value={interviewForm.notes} rows="4" placeholder="예: 투입시 장비지참, 윈도11프로 설치"></textarea>
        </div>
      </div>
      <div class="modal-footer">
        <div class="modal-actions-right">
          <button class="modal-btn cancel" on:click={() => (isInterviewModalOpen = false)}>취소</button>
          <button class="modal-btn primary" on:click={submitInterview} disabled={isSubmittingInterview}>
            {isSubmittingInterview ? '등록 중...' : '등록'}
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

{#if isEmailModalOpen && selectedApplicant}
  <div class="modal-overlay" on:click={closeEmailModal} on:keydown={(e) => e.key === 'Escape' && closeEmailModal()}>
    <div class="modal-container email-modal" on:click|stopPropagation on:keydown={(e) => e.key === 'Escape' && closeEmailModal()}>
      <div class="modal-header">
        <h3>이메일 전송</h3>
        <button class="close-modal-btn" on:click={closeEmailModal}>✕</button>
      </div>
      
      <div class="modal-content">
        {#if emailSendSuccess}
          <div class="email-success">
            <div class="success-icon">✓</div>
            <p>이메일이 성공적으로 전송되었습니다!</p>
          </div>
        {:else if emailSendError}
          <div class="email-error">
            <div class="error-icon">!</div>
            <p>이메일 전송 실패: {emailSendError}</p>
            <button class="retry-btn" on:click={() => sendEmail(selectedApplicant, emailSubject, emailContent)}>
              다시 시도
            </button>
          </div>
        {:else}
          <div class="email-form">
            <div class="form-group">
              <label for="email-to">받는 사람</label>
              <input type="email" id="email-to" value="{selectedApplicant.email}" readonly />
              {#if !selectedApplicant.email || selectedApplicant.email === '-'}
                <div class="email-warning">
                  ⚠️ 지원자의 이메일 주소가 없습니다. 기본 주소({emailConfig.default_receiver})로 전송됩니다.
                </div>
              {/if}
            </div>
            <div class="form-group">
              <label for="email-subject">제목</label>
              <input type="text" id="email-subject" bind:value={emailSubject} />
            </div>
            <div class="form-group">
              <label for="email-content">내용</label>
              <textarea id="email-content" bind:value={emailContent} rows="12"></textarea>
            </div>
          </div>
        {/if}
      </div>
      
      <div class="modal-footer">
        <button class="modal-btn cancel" on:click={closeEmailModal} disabled={isSendingEmail}>
          취소
        </button>
        {#if !emailSendSuccess}
          <button 
            class="modal-btn primary" 
            on:click={() => sendEmail(selectedApplicant, emailSubject, emailContent)}
            disabled={isSendingEmail}
          >
            {#if isSendingEmail}
              <span class="loading-spinner-small"></span>
              전송 중...
            {:else}
              이메일 전송
            {/if}
          </button>
        {/if}
      </div>
    </div>
  </div>
{/if}

{#if isBulkUpdateModalOpen}
  <div class="modal-overlay" on:click={closeBulkUpdateModal} on:keydown={(e) => e.key === 'Escape' && closeBulkUpdateModal()}>
    <div class="modal-container bulk-modal" on:click|stopPropagation on:keydown={(e) => e.key === 'Escape' && closeBulkUpdateModal()}>
      <div class="modal-header">
        <h3>일괄 상태 변경 ({selectedApplicants.length}명)</h3>
        <button class="close-modal-btn" on:click={closeBulkUpdateModal}>✕</button>
      </div>

      <div class="modal-content">
        <div class="selected-applicants-list">
          <h4>선택된 지원자</h4>
          <div class="applicants-list-container">
            {#each applicants.filter(a => selectedApplicants.includes(a.id)) as applicant}
              <div class="selected-applicant-item">
                <div class="applicant-name">{applicant.name || '이름 없음'}</div>
                <div class="applicant-status">
                  {statusOptions.find(s => s.id === applicant.status)?.label || '상태 없음'}
                </div>
                <div class="applicant-project">{applicant.projectTitle || '프로젝트명 없음'}</div>
                <button
                  class="remove-applicant-btn"
                  on:click|stopPropagation={() => toggleSelectApplicant(applicant.id)}
                  title="선택 해제"
                >✕</button>
              </div>
            {/each}
          </div>
        </div>

        <div class="form-group">
          <label for="bulk-status">변경할 상태</label>
          <select id="bulk-status" bind:value={bulkUpdateStatus} class="bulk-select">
            {#each statusOptions as option}
              <option value={option.id}>{option.label}</option>
            {/each}
          </select>
        </div>

        <div class="bulk-warning">
          ⚠️ 이 작업은 취소할 수 없으며, 선택한 모든 지원자의 상태가 변경됩니다.
        </div>
      </div>

      <div class="modal-footer">
        <button class="modal-btn cancel" on:click={closeBulkUpdateModal}>취소</button>
        <button class="modal-btn primary" on:click={bulkUpdateApplicantsStatus}>
          상태 변경하기
        </button>
      </div>
    </div>
  </div>
{/if}

{#if isProjectBulkUpdateModalOpen}
  <div class="modal-overlay" on:click={closeProjectBulkUpdateModal} on:keydown={(e) => e.key === 'Escape' && closeProjectBulkUpdateModal()}>
    <div class="modal-container bulk-modal" on:click|stopPropagation on:keydown={(e) => e.key === 'Escape' && closeProjectBulkUpdateModal()}>
      <div class="modal-header">
        <h3>일괄 상태 변경 ({selectedApplicantProjects.length}건)</h3>
        <button class="close-modal-btn" on:click={closeProjectBulkUpdateModal}>✕</button>
      </div>

      <div class="modal-content">
        <div class="selected-applicants-list">
          <h4>선택된 공고</h4>
          <div class="applicants-list-container">
            {#each filteredApplicantProjects.filter(a => selectedApplicantProjects.includes(a.id)) as project}
              <div class="selected-applicant-item">
                <div class="applicant-name">{project.projectTitle || '공고명 없음'}</div>
                <div class="applicant-status">
                  {statusOptions.find(s => s.id === project.status)?.label || '상태 없음'}
                </div>
                <div class="applicant-project">
                  {project.applied_at ? new Date(project.applied_at).toLocaleDateString('ko-KR') : '-'}
                </div>
                <button
                  class="remove-applicant-btn"
                  on:click|stopPropagation={() => {
                    selectedApplicantProjects = selectedApplicantProjects.filter(id => id !== project.id);
                    if (selectedApplicantProjects.length === 0) closeProjectBulkUpdateModal();
                  }}
                  title="선택 해제"
                >✕</button>
              </div>
            {/each}
          </div>
        </div>

        <div class="form-group">
          <label for="project-bulk-status">변경할 상태</label>
          <select id="project-bulk-status" bind:value={projectBulkUpdateStatus} class="bulk-select">
            {#each statusOptions as option}
              <option value={option.id}>{option.label}</option>
            {/each}
          </select>
        </div>

        <div class="bulk-warning">
          ⚠️ 이 작업은 취소할 수 없으며, 선택한 모든 공고의 상태가 변경됩니다.
        </div>
      </div>

      <div class="modal-footer">
        <button class="modal-btn cancel" on:click={closeProjectBulkUpdateModal}>취소</button>
        <button class="modal-btn primary" on:click={bulkUpdateApplicantProjectsStatus}>
          상태 변경하기
        </button>
      </div>
    </div>
  </div>
{/if}

<FormModal
  bind:isOpen={isBlacklistModalOpen}
  title="블랙리스트 등록"
  submitText="등록"
  cancelText="취소"
  size="sm"
  onSubmit={setBlacklist}
  onCancel={closeBlacklistModal}
>
  <div class="blacklist-form">
    <p><strong>지원자:</strong> {selectedApplicantForBlacklist?.name}</p>
    
    <div class="form-group">
      <label for="blacklistReason">블랙리스트 사유</label>
      <textarea
        id="blacklistReason"
        bind:value={blacklistReason}
        placeholder="블랙리스트 등록 사유를 입력하세요"
        rows="4"
      ></textarea>
    </div>
  </div>
</FormModal>

{#if isProfileFilesModalOpen}
  <div class="modal-overlay" on:click={closeProfileFilesModal} on:keydown={(e) => e.key === 'Escape' && closeProfileFilesModal()}>
    <div class="modal-container profile-files-modal" on:click|stopPropagation on:keydown={(e) => e.key === 'Escape' && closeProfileFilesModal()}>
      <div class="modal-header">
        <h3>첨부 문서</h3>
        <button class="close-modal-btn" on:click={closeProfileFilesModal}>✕</button>
      </div>
      
      <div class="modal-content">
        {#if isLoadingFiles}
          <div class="loading-container">
            <div class="loading-spinner"></div>
            <div class="loading-text">파일을 불러오는 중...</div>
          </div>
        {:else if fileLoadError}
          <div class="error-container">
            <div class="error-icon">!</div>
            <div class="error-text">{fileLoadError}</div>
          </div>
        {:else if selectedApplicantFiles.length === 0}
          <div class="empty-state">
            <div class="empty-icon">📄</div>
            <p>업로드된 첨부 문서가 없습니다.</p>
          </div>
        {:else}
          <div class="files-list">
            <div class="files-header">
              <h4>업로드된 파일 ({selectedApplicantFiles.length}개)</h4>
            </div>
            
            <div class="files-grid">
              {#each selectedApplicantFiles as file (file.id)}
                <div class="file-item">
                  <div class="file-icon">
                    {#if file.mime_type?.includes('pdf')}
                      📄
                    {:else if file.mime_type?.includes('image')}
                      🖼️
                    {:else if file.mime_type?.includes('word') || file.mime_type?.includes('document')}
                      📝
                    {:else if file.mime_type?.includes('excel') || file.mime_type?.includes('sheet')}
                      📊
                    {:else}
                      📎
                    {/if}
                  </div>
                  
                  <div class="file-info">
                    <div class="file-name" title={file.original_name}>
                      {file.original_name}
                    </div>
                    <div class="file-details">
                      <span class="file-size">
                        {#if file.file_size}
                          {(file.file_size / 1024 / 1024).toFixed(2)} MB
                        {:else}
                          크기 정보 없음
                        {/if}
                      </span>
                      <span class="file-type">
                        {file.file_type || '일반'}
                      </span>
                      <span class="file-date">
                        {#if file.created_at}
                          {new Date(file.created_at).toLocaleDateString('ko-KR')}
                        {:else}
                          날짜 정보 없음
                        {/if}
                      </span>
                    </div>
                    {#if file.description}
                      <div class="file-description">
                        {file.description}
                      </div>
                    {/if}
                  </div>
                  
                  <div class="file-actions">
                    <button 
                      class="download-btn" 
                      on:click={() => downloadFile(file, selectedApplicant || { userId: file.user_id })}
                      title="파일 다운로드"
                    >
                      ⬇️ 다운로드
                    </button>
                  </div>
                </div>
              {/each}
            </div>
          </div>
        {/if}
      </div>
      
      <div class="modal-footer">
        <button class="modal-btn cancel" on:click={closeProfileFilesModal}>닫기</button>
      </div>
    </div>
  </div>
{/if}

{#if isProjectDetailModalOpen}
  <div class="modal-overlay" on:click={closeProjectDetailModal} on:keydown={(e) => e.key === 'Escape' && closeProjectDetailModal()}>
    <div class="modal-container" on:click|stopPropagation on:keydown={(e) => e.key === 'Escape' && closeProjectDetailModal()}>
      <div class="modal-header">
        <h3>공고 상세 정보</h3>
        <button class="close-modal-btn" on:click={closeProjectDetailModal}>✕</button>
      </div>
      
      <div class="modal-content">
        {#if isLoadingProjectDetail}
          <div class="loading-container">
            <div class="loading-spinner"></div>
            <span>불러오는 중...</span>
          </div>
        {:else if projectDetailError}
          <div class="error-container">{projectDetailError}</div>
        {:else if projectDetail}
          <div class="detail-section">
            <h4>기본 정보</h4>
            <div class="detail-grid">
              <div class="detail-item">
                <span class="detail-label">공고명</span>
                <span class="detail-value">{projectDetail.title || '-'}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">회사명</span>
                <span class="detail-value">{projectDetail.company || '-'}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">카테고리</span>
                <span class="detail-value">{projectDetail.category || '-'}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">상태</span>
                <span class="detail-value status-{projectDetail.status}">
                  {(() => {
                    const statusLabels = {
                      'active': '모집중',
                      'deleted': '삭제됨',
                      'open': '채용 시 마감',
                      'closed': '상시 채용'
                    };
                    return statusLabels[projectDetail.status] || projectDetail.status;
                  })()}
                </span>
              </div>
              <div class="detail-item">
                <span class="detail-label">개발자 등급/경력</span>
                <span class="detail-value">{projectDetail.level || '-'}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">모집인원</span>
                <span class="detail-value">{projectDetail.headcount || '-'}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">근무지</span>
                <span class="detail-value">{projectDetail.location || '-'}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">단가/연봉</span>
                <span class="detail-value">{projectDetail.salary || '-'}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">시작일</span>
                <span class="detail-value">
                  {(() => {
                    const startDate = projectDetail.start_date || projectDetail.startDate;
                    return startDate ? new Date(startDate).toLocaleDateString('ko-KR') : '-';
                  })()}
                </span>
              </div>
              <div class="detail-item">
                <span class="detail-label">종료일</span>
                <span class="detail-value">
                  {(() => {
                    const endDate = projectDetail.end_date || projectDetail.endDate || projectDetail.deadline;
                    return endDate ? new Date(endDate).toLocaleDateString('ko-KR') : '-';
                  })()}
                </span>
              </div>
              <div class="detail-item">
                <span class="detail-label">등록일</span>
                <span class="detail-value">
                  {projectDetail.created_at ? new Date(projectDetail.created_at).toLocaleDateString('ko-KR') : '-'}
                </span>
              </div>
              <div class="detail-item">
                <span class="detail-label">등록자</span>
                <span class="detail-value">{projectDetail.creator_name || '-'}</span>
              </div>
            </div>
          </div>

          <!-- 기술 스택 -->
          <div class="detail-section">
            <h4>기술 스택</h4>
            <div class="detail-content">
              <div class="skills-list">
                {#if projectDetail.skills && projectDetail.skills.length > 0}
                  {#each projectDetail.skills as skill}
                    <span class="skill-tag">{skill}</span>
                  {/each}
                {:else if projectDetail.skills && typeof projectDetail.skills === 'string'}
                  {#each projectDetail.skills.split(',') as skill}
                    <span class="skill-tag">{skill.trim()}</span>
                  {/each}
                {:else}
                  <span class="no-data">등록된 기술 스택이 없습니다</span>
                {/if}
              </div>
            </div>
          </div>

          <!-- 자격 요건 -->
          <div class="detail-section">
            <h4>자격 요건</h4>
            <div class="detail-content">
              <div class="requirements-list">
                {#if projectDetail.requirements && projectDetail.requirements.length > 0}
                  {#each projectDetail.requirements as requirement}
                    <div class="requirement-item">• {requirement}</div>
                  {/each}
                {:else if projectDetail.requirements && typeof projectDetail.requirements === 'string'}
                  {#each projectDetail.requirements.split('\n') as requirement}
                    <div class="requirement-item">• {requirement.trim()}</div>
                  {/each}
                {:else}
                  <span class="no-data">등록된 자격 요건이 없습니다</span>
                {/if}
              </div>
            </div>
          </div>

          <!-- 우대 사항 -->
          <div class="detail-section">
            <h4>우대 사항</h4>
            <div class="detail-content">
              <div class="preferred-list">
                {#if projectDetail.preferred_skills && projectDetail.preferred_skills.length > 0}
                  {#each projectDetail.preferred_skills as preferred}
                    <div class="preferred-item">• {preferred}</div>
                  {/each}
                {:else if projectDetail.preferred_skills && typeof projectDetail.preferred_skills === 'string'}
                  {#each projectDetail.preferred_skills.split('\n') as preferred}
                    <div class="preferred-item">• {preferred.trim()}</div>
                  {/each}
                {:else}
                  <span class="no-data">등록된 우대 사항이 없습니다</span>
                {/if}
              </div>
            </div>
          </div>

          <!-- 프로젝트 설명 -->
          <div class="detail-section">
            <h4>프로젝트 상세 설명</h4>
            <div class="detail-content">
              <div class="description-content">
                {#if projectDetail.description}
                  <p>{projectDetail.description}</p>
                {:else}
                  <span class="no-data">등록된 상세 설명이 없습니다</span>
                {/if}
              </div>
            </div>
          </div>

          <!-- 연락처 정보 -->
          <div class="detail-section">
            <h4>연락처 정보</h4>
            <div class="detail-content">
              <div class="contact-content">
                {#if projectDetail.contact}
                  <p>{projectDetail.contact}</p>
                {:else}
                  <span class="no-data">등록된 연락처 정보가 없습니다</span>
                {/if}
              </div>
            </div>
          </div>

          <!-- 관리자 비고 (관리자만 표시) -->
          {#if isAdmin}
            <div class="detail-section admin-notes-section">
              <h4 class="admin-notes-title">
                👑 관리자 비고
              </h4>
              <div class="detail-content admin-notes-content">
                <div class="admin-notes-text">
                  {#if projectDetail.admin_notes}
                  <p><strong>비고:</strong> {projectDetail.admin_notes}</p>
                {:else}
                  <span class="no-data">등록된 관리자 비고가 없습니다</span>
                {/if}
                
                {#if projectDetail.admin_remark}
                  <p><strong>비고 2:</strong> {projectDetail.admin_remark}</p>
                {/if}
                </div>
              </div>
            </div>
          {/if}
        {:else}
          <div class="no-data">공고 정보를 찾을 수 없습니다.</div>
        {/if}
      </div>
      
    </div>
  </div>
{/if}

<style>
  /* 기본 스타일 */
  .admin-applicants-container {
    padding: 0 var(--spacing-lg);
    max-width: 1800px;
    margin: 0 auto;
  }

  /* AG Grid 스타일 */
  .applicants-grid {
    margin-top: 1rem;
  }

  
  .page-header {
    margin-bottom: var(--spacing-lg);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--spacing-md);
  }
  
  .page-title {
    font-size: 2rem;
  }
  
  /* 메인 컨텐츠 그리드 */
  .admin-content-grid {
    display: grid;
    grid-template-columns: 300px 1fr;
    gap: var(--spacing-lg);
  }
  
  /* 좌측 섹션 (공고/지원자 목록) */
  .admin-left-section {
    background-color: var(--color-white, #fff);
    border: 1px solid var(--color-gray-200, #e9ecef);
    border-radius: var(--border-radius-lg, 8px);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .admin-project-list-section {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }
  
  .admin-project-list {
    max-height: calc(100vh - 300px); /* 헤더/푸터 등 높이 고려 */
    overflow-y: auto;
    flex-grow: 1;
  }
  
  /* 우측 섹션 (지원자 상세) */
  .applicants-section {
    background-color: var(--color-white, #fff);
    padding: var(--spacing-lg);
    border: 1px solid var(--color-gray-200, #e9ecef);
    border-radius: var(--border-radius-lg, 8px);
    max-height: 830px;
    overflow-y: auto;
  }

  .applicants-header h3 {
    margin-bottom: var(--spacing-md);
  }


  /* 반응형 스타일 */
  @media (max-width: 992px) {
    .admin-content-grid {
      grid-template-columns: 250px 1fr;
    }
  }

  @media (max-width: 767px) {
    .admin-applicants-container {
      padding: 0 var(--spacing-sm);
    }
    
    .page-header {
      padding: 0 var(--spacing-md);
      flex-direction: column;
      align-items: flex-start;
    }

    .admin-content-grid {
      display: block;
    }

    .admin-left-section {
      margin-bottom: var(--spacing-lg);
    }
    
    .admin-project-list {
      max-height: 35vh;
    }

    .applicants-section {
      padding: var(--spacing-md);
    }


  }

  :global(.logo h1) {
    color: #3273dc;
    font-size: 2rem;
    font-weight: 700;
    text-transform: none;
    cursor: pointer;
  }
  


  /* 첨부 문서 모달 */
  .profile-files-modal {
    max-width: 800px;
    max-height: 80vh;
  }
  .files-list {
    max-height: 400px;
    overflow-y: auto;
  }
  .files-header {
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #dee2e6;
  }
  .files-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .file-item {
    display: flex;
    align-items: center;
    padding: 12px;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    background: #f8f9fa;
    transition: background-color 0.2s;
  }
  .file-item:hover {
    background: #e9ecef;
  }
  .file-icon {
    font-size: 24px;
    margin-right: 12px;
    flex-shrink: 0;
  }
  .file-info {
    flex: 1;
    min-width: 0;
  }
  .file-name {
    font-weight: 600;
    color: #495057;
    margin-bottom: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .file-details {
    display: flex;
    gap: 12px;
    font-size: 12px;
    color: #6c757d;
    flex-wrap: wrap;
  }
  .file-details span {
    background: #ffffff;
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid #dee2e6;
  }
  .file-description {
    margin-top: 6px;
    font-size: 14px;
    color: #6c757d;
    font-style: italic;
  }
  .file-actions {
    flex-shrink: 0;
    margin-left: 12px;
  }
  .download-btn {
    background: #28a745;
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: background-color 0.2s;
  }
  .download-btn:hover {
    background: #218838;
  }

  /* 필터 옵션 컨테이너 */
  .filter-options {
    display: flex;
    align-items: center;
    gap: var(--spacing-md, 16px);
  }
  
  .checkbox-group {
    display: flex;
    align-items: center;
    gap: var(--spacing-lg, 24px);
    padding: var(--spacing-sm, 8px) var(--spacing-md, 16px);
    background: var(--color-gray-50, #f8f9fa);
    border-radius: var(--border-radius, 8px);
    border: 1px solid var(--color-gray-200, #e9ecef);
  }
  
  /* 모던 체크박스 스타일 */
  .modern-checkbox {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs, 8px);
    cursor: pointer;
    user-select: none;
    position: relative;
    font-size: 14px;
    color: var(--color-gray-700, #495057);
    transition: all 0.2s ease;
  }
  
  .modern-checkbox:hover {
    color: var(--color-primary, #007bff);
  }
  
  .modern-checkbox input[type="checkbox"] {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }
  
  .checkbox-custom {
    position: relative;
    width: 18px;
    height: 18px;
    background-color: white;
    border: 2px solid var(--color-gray-300, #ced4da);
    border-radius: 4px;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }
  
  .modern-checkbox input[type="checkbox"]:checked + .checkbox-custom {
    background-color: var(--color-primary, #007bff);
    border-color: var(--color-primary, #007bff);
  }
  
  .modern-checkbox input[type="checkbox"]:checked + .checkbox-custom::after {
    content: '';
    position: absolute;
    left: 5px;
    top: 1px;
    width: 6px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
  
  .modern-checkbox:hover .checkbox-custom {
    border-color: var(--color-primary, #007bff);
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
  
  .checkbox-label {
    font-weight: 500;
    transition: color 0.2s ease;
  }
  
  .modern-checkbox input[type="checkbox"]:checked + .checkbox-custom + .checkbox-label {
    color: var(--color-primary, #007bff);
  }


  /* 모달 제목 흰색 스타일 */
  .modal-header h3 {
    color: white;
  }

  /* ======= 모바일 카드 레이아웃 ======= */
  :global(.mobile-grid) {
    height: auto !important;
  }
  :global(.mobile-card) {
    padding: 12px;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
    margin: 3px 0;
  }
  :global(.mobile-card .card-header) {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    margin-bottom: 8px;
  }
  :global(.mobile-card .name) {
    font-size: 15px !important;
    font-weight: 600;
    margin-bottom: 0;
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  :global(.mobile-card .card-body) {
    margin-top: 8px;
  }
  :global(.mobile-card .card-row) {
    padding: 1px 0;
    font-size: 13px;
    line-height: 1.4;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  /* ===== 지원자 블랙리스트 태그 ===== */
  :global(.applicant-blacklist-tag) {
    background-color: #dc3545;
    color: white;
    padding: 1px 6px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.2;
    flex-shrink: 0;
  }

  /* ===== 모바일 전용 UI 요소들 ===== */
  :global(.checkbox-container) {
    display: flex;
    align-items: center;
    margin-right: 8px;
  }

  :global(.mobile-checkbox) {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: var(--primary-color);
  }

  :global(.status-row) {
    border-top: 1px solid #e9ecef;
    padding-top: 4px !important;
    margin-top: 4px;
  }

  :global(.mobile-status-select) {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 12px;
    min-width: 100px;
  }

  :global(.attachment-row) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 4px;
    padding-top: 4px !important;
    border-top: 1px solid #e9ecef;
  }

  :global(.actions-row) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 4px;
    padding-top: 4px !important;
    border-top: 1px solid #e9ecef;
  }

  /* 첨부 문서 버튼 모바일 스타일 */
  :global(.profile-docs-btn) {
    border: none;
    padding: 15px 25px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 15px !important;
    transition: all 0.2s ease;
    font-weight: 600;
    min-width: 120px;
    height: 50px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
  
  :global(.profile-docs-btn .doc-icon) {
    font-size: 18px;
    display: inline-block;
  }
  
  :global(.detail-btn .detail-icon) {
    font-size: 18px;
    display: inline-block;
  }
  
  /* 파일이 있는 경우 (파란색) */
  :global(.profile-docs-btn[style*="#4A90E2"]) {
    background: #4A90E2 !important;
    color: white !important;
  }
  :global(.profile-docs-btn[style*="#4A90E2"]:hover) {
    background: #357ABD !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(74, 144, 226, 0.3);
  }
  
  /* 파일이 없는 경우 (회색) */
  :global(.profile-docs-btn[style*="#6c757d"]) {
    background: #6c757d !important;
    color: white !important;
  }
  :global(.profile-docs-btn[style*="#6c757d"]:hover) {
    background: #5a6268 !important;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(108, 117, 125, 0.3);
  }

  /* 상세 버튼 모바일 스타일 */
  :global(.detail-btn) {
    background: #28a745;
    color: white;
    border: none;
    padding: 15px 25px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 15px !important;
    transition: all 0.2s ease;
    font-weight: 600;
    min-width: 120px;
    height: 50px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
  :global(.detail-btn:hover) {
    background: #218838;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(40, 167, 69, 0.3);
  }

  /* 체크박스 셀 스타일 */
  :global(.checkbox-cell) {
    display: flex !important;
    align-items: center;
    justify-content: center;
    padding: 0 !important;
  }

  :global(.ag-selection-checkbox) {
    margin: 0 !important;
  }

  :global(.ag-header-select-all) {
    margin: 0 !important;
  }


  /* applicants-grid 및 테이블 관련 스타일 제거 - AG-Grid로 대체 */

  /* 반응형 스타일 */
  @media (max-width: 992px) {
    .admin-content-grid {
      grid-template-columns: 250px 1fr;
    }

    /* 모바일 테이블 스타일 제거 - AG-Grid 사용 */
  }

  :global(.logo h1) {
    color: #3273dc;
    font-size: 2rem;
    font-weight: 700;
    text-transform: none;
    cursor: pointer;
  }
  
  :global(.profile-docs-btn) {
    padding: 4px 8px;
    font-size: 12px;
  }
  
  /* AG Grid 내부 버튼을 셀에 가득 차게 */
  :global(.ag-cell .profile-docs-btn) {
    width: 100% !important;
    height: 100% !important;
    min-width: unset !important;
    min-height: 35px !important;
    margin: 2px !important;
    box-sizing: border-box !important;
  }
  
  /* AG Grid 내부 상세 버튼도 셀에 가득 차게 */
  :global(.ag-cell .detail-btn) {
    width: 100% !important;
    height: 100% !important;
    min-width: unset !important;
    min-height: 35px !important;
    margin: 2px !important;
    box-sizing: border-box !important;
    font-size: 15px !important;
  }
  
  /* 상세 버튼 폰트 크기 강제 적용 */
  :global(.detail-btn.small) {
    font-size: 15px !important;
  }
  
  :global(button.detail-btn) {
    font-size: 15px !important;
  }

  /* 데스크톱 테이블 스타일 제거 - AG-Grid 사용 */

  /* 첨부 문서 모달 */
  .profile-files-modal {
    max-width: 800px;
    max-height: 80vh;
  }
  .files-list {
    max-height: 400px;
    overflow-y: auto;
  }
  .files-header {
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #dee2e6;
  }
  .files-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .file-item {
    display: flex;
    align-items: center;
    padding: 12px;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    background: #f8f9fa;
    transition: background-color 0.2s;
  }
  .file-item:hover {
    background: #e9ecef;
  }
  .file-icon {
    font-size: 24px;
    margin-right: 12px;
    flex-shrink: 0;
  }
  .file-info {
    flex: 1;
    min-width: 0;
  }
  .file-name {
    font-weight: 600;
    color: #495057;
    margin-bottom: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .file-details {
    display: flex;
    gap: 12px;
    font-size: 12px;
    color: #6c757d;
    flex-wrap: wrap;
  }
  .file-details span {
    background: #ffffff;
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid #dee2e6;
  }
  .file-description {
    margin-top: 6px;
    font-size: 14px;
    color: #6c757d;
    font-style: italic;
  }
  .file-actions {
    flex-shrink: 0;
    margin-left: 12px;
  }
  .download-btn {
    background: #28a745;
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: background-color 0.2s;
  }
  .download-btn:hover {
    background: #218838;
  }

  /* 필터 옵션 컨테이너 */
  .filter-options {
    display: flex;
    align-items: center;
    gap: var(--spacing-md, 16px);
  }
  
  .checkbox-group {
    display: flex;
    align-items: center;
    gap: var(--spacing-lg, 24px);
    padding: var(--spacing-sm, 8px) var(--spacing-md, 16px);
    background: var(--color-gray-50, #f8f9fa);
    border-radius: var(--border-radius, 8px);
    border: 1px solid var(--color-gray-200, #e9ecef);
  }
  
  /* 모던 체크박스 스타일 */
  .modern-checkbox {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs, 8px);
    cursor: pointer;
    user-select: none;
    position: relative;
    font-size: 14px;
    color: var(--color-gray-700, #495057);
    transition: all 0.2s ease;
  }
  
  .modern-checkbox:hover {
    color: var(--color-primary, #007bff);
  }
  
  .modern-checkbox input[type="checkbox"] {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }
  
  .checkbox-custom {
    position: relative;
    width: 18px;
    height: 18px;
    background-color: white;
    border: 2px solid var(--color-gray-300, #ced4da);
    border-radius: 4px;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }
  
  .modern-checkbox input[type="checkbox"]:checked + .checkbox-custom {
    background-color: var(--color-primary, #007bff);
    border-color: var(--color-primary, #007bff);
  }
  
  .modern-checkbox input[type="checkbox"]:checked + .checkbox-custom::after {
    content: '';
    position: absolute;
    left: 5px;
    top: 1px;
    width: 6px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
  
  .modern-checkbox:hover .checkbox-custom {
    border-color: var(--color-primary, #007bff);
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
  
  .checkbox-label {
    font-weight: 500;
    transition: color 0.2s ease;
  }
  
  .modern-checkbox input[type="checkbox"]:checked + .checkbox-custom + .checkbox-label {
    color: var(--color-primary, #007bff);
  }


  /* 모달 제목 흰색 스타일 */
  .modal-header h3 {
    color: white;
  }

  /* ======= 모바일 카드 레이아웃 ======= */
  :global(.mobile-grid) {
    height: auto !important;
  }
  :global(.mobile-card) {
    padding: 12px;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
    margin: 3px 0;
  }
  :global(.mobile-card .card-header) {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    margin-bottom: 8px;
  }
  :global(.mobile-card .name) {
    font-size: 15px !important;
    font-weight: 600;
    margin-bottom: 0;
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  :global(.mobile-card .card-body) {
    margin-top: 8px;
  }
  :global(.mobile-card .card-row) {
    padding: 1px 0;
    font-size: 13px;
    line-height: 1.4;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  /* ===== 지원자 블랙리스트 태그 ===== */
  :global(.applicant-blacklist-tag) {
    background-color: #dc3545;
    color: white;
    padding: 1px 6px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.2;
    flex-shrink: 0;
  }

  /* ===== 모바일 전용 UI 요소들 ===== */
  :global(.checkbox-container) {
    display: flex;
    align-items: center;
    margin-right: 8px;
  }

  :global(.mobile-checkbox) {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: var(--primary-color);
  }

  :global(.status-row) {
    border-top: 1px solid #e9ecef;
    padding-top: 4px !important;
    margin-top: 4px;
  }

  :global(.mobile-status-select) {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 12px;
    min-width: 100px;
  }

  :global(.attachment-row) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 4px;
    padding-top: 4px !important;
    border-top: 1px solid #e9ecef;
  }

  :global(.actions-row) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 4px;
    padding-top: 4px !important;
    border-top: 1px solid #e9ecef;
  }

  /* 첨부 문서 버튼 모바일 스타일 */
  :global(.profile-docs-btn) {
    border: none;
    padding: 15px 25px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 15px !important;
    transition: all 0.2s ease;
    font-weight: 600;
    min-width: 120px;
    height: 50px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
  
  :global(.profile-docs-btn .doc-icon) {
    font-size: 18px;
    display: inline-block;
  }
  
  :global(.detail-btn .detail-icon) {
    font-size: 18px;
    display: inline-block;
  }
  
  /* 파일이 있는 경우 (파란색) */
  :global(.profile-docs-btn[style*="#4A90E2"]) {
    background: #4A90E2 !important;
    color: white !important;
  }
  :global(.profile-docs-btn[style*="#4A90E2"]:hover) {
    background: #357ABD !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(74, 144, 226, 0.3);
  }
  
  /* 파일이 없는 경우 (회색) */
  :global(.profile-docs-btn[style*="#6c757d"]) {
    background: #6c757d !important;
    color: white !important;
  }
  :global(.profile-docs-btn[style*="#6c757d"]:hover) {
    background: #5a6268 !important;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(108, 117, 125, 0.3);
  }

  /* 상세 버튼 모바일 스타일 */
  :global(.detail-btn) {
    background: #28a745;
    color: white;
    border: none;
    padding: 15px 25px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 15px !important;
    transition: all 0.2s ease;
    font-weight: 600;
    min-width: 120px;
    height: 50px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
  :global(.detail-btn:hover) {
    background: #218838;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(40, 167, 69, 0.3);
  }

  /* 체크박스 셀 스타일 */
  :global(.checkbox-cell) {
    display: flex !important;
    align-items: center;
    justify-content: center;
    padding: 0 !important;
  }

  :global(.ag-selection-checkbox) {
    margin: 0 !important;
  }

  :global(.ag-header-select-all) {
    margin: 0 !important;
  }

  .project-title-link {
    color: #3273dc;
    text-decoration: underline;
    background: none;
    border: none;
    cursor: pointer;
    font-size: inherit;
    padding: 0;
  }
  /* 기술 스택 스타일 */
  .skills-list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-sm, 8px);
  }

  .skill-tag {
    background-color: var(--color-primary, #007bff);
    color: var(--color-white, #fff);
    padding: var(--spacing-xs, 4px) var(--spacing-sm, 8px);
    border-radius: var(--border-radius, 4px);
    font-size: 0.875rem;
    font-weight: 500;
  }

  .requirements-list,
  .preferred-list {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs, 4px);
  }

  .requirement-item,
  .preferred-item {
    font-size: 0.95rem;
    color: var(--color-gray-800, #374151);
    line-height: 1.5;
  }

  .description-content,
  .contact-content {
    line-height: 1.6;
  }

  .description-content p,
  .contact-content p {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .no-data {
    color: var(--color-gray-500, #6b7280);
    font-style: italic;
  }

  /* 상태별 색상 */
  .status-active {
    color: var(--color-success, #10b981);
    font-weight: 600;
  }

  .status-deleted {
    color: var(--color-danger, #ef4444);
    font-weight: 600;
  }

  .status-open {
    color: var(--color-warning, #f59e0b);
    font-weight: 600;
  }

  .status-closed {
    color: var(--color-info, #3b82f6);
    font-weight: 600;
  }

  /* 관리자 비고 섹션 스타일 */
  .admin-notes-section {
    border: 2px solid var(--color-primary, #007bff);
    border-radius: 8px;
    background-color: #f8f9ff;
    margin-bottom: 100px;
  }

  .admin-notes-title {
    color: var(--color-primary, #007bff) !important;
    border-bottom-color: var(--color-primary, #007bff) !important;
  }

  .admin-notes-content {
    background-color: #f0f4ff !important;
    border-color: var(--color-primary, #007bff) !important;
  }

  .admin-notes-text {
    font-style: italic;
    color: var(--color-primary, #007bff);
  }
  
  .status-filter-select {
    padding: 6px 12px;
    font-size: 14px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: white;
    cursor: pointer;
    min-width: 120px;
    margin-right: 10px;
  }
  
  /* 일괄 작업 버튼 스타일 */
  .bulk-actions {
    margin-bottom: 16px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }
  
  .bulk-update-btn,
  .bulk-status-btn {
    background: var(--primary-color, #3273dc);
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
  }

  .bulk-update-btn:hover,
  .bulk-status-btn:hover {
    background: #2366d1;
  }

  .bulk-unread-btn {
    background: #6c757d;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
  }
  
  .bulk-unread-btn:hover {
    background: #5a6268;
  }
  
  /* 열람 상태 체크박스 스타일 */
  .read-status-container {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  
  .read-status-checkbox {
    margin: 0 !important;
    cursor: default !important;
    transform: scale(1.2) !important;
  }
  
  .read-status-mobile {
    display: flex;
    align-items: center;
    margin-left: 8px;
    gap: 4px;
  }
  
  .read-status-checkbox-mobile {
    margin: 0;
    cursor: default;
    transform: scale(1.1);
  }
  
  .read-status-label {
    font-size: 10px;
    font-weight: 600;
    color: #495057;
  }
  
  /* 체크박스 색상 커스터마이징 */
  .read-status-checkbox:checked {
    accent-color: #28a745;
  }
  
  .read-status-checkbox:not(:checked) {
    accent-color: #6c757d;
  }
  
  .read-status-checkbox-mobile:checked {
    accent-color: #28a745;
  }
  
  .read-status-checkbox-mobile:not(:checked) {
    accent-color: #6c757d;
  }
  
  /* AG Grid 열람 상태 컨럼 스타일 */
  :global(.ag-theme-alpine .ag-cell .read-status-container) {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  /* 모바일 카드 열람 상태 스타일 */
  :global(.actions-row .read-status-mobile) {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding: 2px 6px;
    border-radius: 4px;
    background-color: rgba(0,0,0,0.05);
    font-size: 11px;
    font-weight: 600;
  }
  
  /* 상태 필터 컨테이너 스타일 */
  .admin-filter-container {
    margin: 10px 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .admin-filter-container label {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    min-width: 40px;
  }
  
  .admin-status-filter {
    padding: 6px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: #fff;
    font-size: 14px;
    color: #333;
    cursor: pointer;
    min-width: 120px;
  }
  
  .admin-status-filter:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
  
  .admin-status-filter:hover {
    border-color: #4a90e2;
  }

  /* 인터뷰 등록 버튼 스타일 */
  .modal-btn.interview {
    background-color: #8b5cf6;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .modal-btn.interview:hover {
    background-color: #7c3aed;
  }

  /* 인터뷰 등록 모달 스타일 */
  .interview-modal {
    max-width: 560px;
  }

  .interview-form-group {
    margin-bottom: 14px;
  }

  .interview-form-group label {
    display: block;
    margin-bottom: 5px;
    font-weight: 600;
    font-size: 0.85rem;
    color: #374151;
  }

  .interview-form-group input,
  .interview-form-group select,
  .interview-form-group textarea {
    width: 100%;
    padding: 9px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.9rem;
    outline: none;
    transition: border-color 0.2s;
    box-sizing: border-box;
  }

  .interview-form-group input:focus,
  .interview-form-group select:focus,
  .interview-form-group textarea:focus {
    border-color: #8b5cf6;
    box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.1);
  }

  .interview-form-group textarea {
    resize: vertical;
  }

  .iv-required {
    color: #ef4444;
  }
</style>